"use client";

import { useEffect, useLayoutEffect, useMemo, useState, type ReactNode } from "react";
import { useSearchParams } from "next/navigation";
import { CalendarRange, ChevronDown, ChevronRight, CircleAlert, CircleHelp, Database, ImageIcon, Info, Layers, Search, Sparkles, User, Users } from "lucide-react";
import {
  AeraAccordion,
  AeraTransferMeta,
  Modal,
  ModalCloseButton,
  ModalShell,
  SegmentedTabSwitcher,
  type SegmentedTabItem,
} from "@aera/ui";
import {
  AeraImage,
  ImageViewer,
  formatAmount,
  formatThaiDateCard,
  formatThaiDatetimePartsFromDate,
  getDisplayText,
  parseDatetimeInput,
  useWideDataLayout,
  type IncomeRecord,
} from "@aera/data-table";
import {
  WORK_BUCKET_LABELS,
  formatAmountMinor,
  surfaceStateLabel,
  workStatusLabel,
  displayWorkTitle,
  slipTransferLabel,
  displayContextValue,
  isReceivableBatchCard,
  isFromBatchCard,
  isBacklogChildPaid,
  isSlipOwnerAskCard,
  askKindFromCard,
  partitionWorkCards,
  amountBasisLabel,
  workDetailBadge,
} from "@/lib/financialWork/nav";
import { formatFinancialWorkErrorMessage } from "@/lib/financialWork/mutationSafety";
import { normalizeFinancialWorkSearch } from "@/lib/financialWork/lineDeepLink";
import { useFinancialWorkWideRail } from "@/lib/financialWork/layout";
import { getUserIdFromAuthToken } from "@/lib/getUserIdFromAuthToken";
import type {
  ActionDirective,
  BacklogChildRow,
  EvidenceItem,
  FinancialWorkNavId,
  LiffFinancialWorkCard,
  OperationRecoveryState,
  OutstandingReminderDraft,
  OutstandingSlice,
  WorkQueueProjection,
  WorkTimelineEvent,
} from "@/lib/financialWork/types";
import { FinancialWorkDocThumb, type DocThumbGlyph } from "@/app/financial-work/FinancialWorkDocThumb";
import { resolveEvidenceItemImageUrl, resolveQueueCardImageUrl } from "@/lib/financialWork/queueCardThumb";
import { useFinancialWorkMutationGate } from "@/hooks/useFinancialWorkMutationGate";
import { useFinancialWorkIncomeRecord, financialWorkIncomeRecordTarget } from "@/hooks/useFinancialWorkIncomeRecord";
import { closedProvenanceSourceLabel } from "@/lib/financialWork/incomeRecordSourceLabel";
import { readIncomeTransferMinor } from "@/lib/financialWork/incomeRecordQueueDisplay";
import { AeraButton } from "@/app/components/ui/AeraButton";
import { ReminderToneDrafts } from "@/app/components/ReminderToneDrafts";
import { showToast } from "@/app/components/ui/Toast";
import type { QueueErrorKind } from "@/lib/financialWork/queueClient";
import {
  getReceivableOrderId,
  matchReceivablePayment,
  pickReceivableSlipImageUrl,
  type ReceivableListItem,
} from "@/lib/pendingOrders";
import { filterReceivableMoneyCandidates } from "@/lib/receivableDisplayStatus";
import { offerMoneyUndoToast, pickMoneyUndoSourceLabel } from "@/lib/moneyUndoToast";
import { notifyIncomeMoneyChanged } from "@/lib/incomeMoneyRefresh";
import { fetchCombinedWorkspaceTransactionSummary } from "@/lib/transactionSummary";
import { FinancialWorkDesktopQueue } from "./FinancialWorkDesktopQueue";
import { FinancialWorkPreparingStatus } from "./FinancialWorkPreparingStatus";
export { FinancialWorkPreparingStatus } from "./FinancialWorkPreparingStatus";
import { FinancialWorkSectionHeader } from "@/app/financial-work/FinancialWorkSectionHeader";
import { DataSourcesStatusControl } from "@/app/financial-work/DataSourcesStatusControl";

function bahtLabelFromMajor(amount: number): string {
  const rounded = Math.round(amount);
  const abs = Math.abs(rounded).toLocaleString("th-TH");
  return rounded < 0 ? `−฿${abs}` : `฿${abs}`;
}

/** Buddhist-era day+month+year — matches billlin `thai_day_month`. */
function formatThaiDayMonthYear(iso?: string | null): string {
  if (!iso?.trim()) return "";
  const label = formatThaiDateCard(iso);
  return !label || label === "—" ? "" : label;
}

function buildSplitPlanLineFromRows(
  rows: Array<{ label: string; amountMinor?: number | null; occurredAt?: string }>,
): string {
  const parts: string[] = [];
  for (const row of rows) {
    const day = formatThaiDayMonthYear(row.occurredAt);
    const amt =
      row.amountMinor != null && Number.isFinite(row.amountMinor)
        ? formatAmountMinor(row.amountMinor, "THB")
            .replace(/^฿\s*/, "")
            .replace(/,/g, "")
        : "";
    if (day && amt) parts.push(`${day} (${amt})`);
    else if (amt) parts.push(`(${amt})`);
  }
  return parts.join(" + ");
}

function SplitPlanDetailLine({ line }: { line: string }) {
  const chunks = line.split(/\s*\+\s*/).filter(Boolean);
  return (
    <p
      className="mt-1 text-xs text-aera-gray"
      data-testid="fw-split-plan-detail"
    >
      {chunks.map((chunk, index) => {
        const match = chunk.match(/^(.*?)\s*\(([^)]+)\)\s*$/);
        return (
          <span key={`${index}-${chunk}`}>
            {index > 0 ? " + " : null}
            {match ? (
              <>
                {match[1]}{" "}
                <span className="font-semibold text-aera-black">
                  ({match[2]})
                </span>
              </>
            ) : (
              chunk
            )}
          </span>
        );
      })}
    </p>
  );
}

function evidenceDetailTitle(item: EvidenceItem): string {
  const sourceMeta = item.sourceLabel?.trim() || "";
  const label = item.label?.trim() || "";
  const transferor = item.transferorLabel?.trim() || "";
  if (
    label === "สลิป" ||
    /^สลิป/.test(label) ||
    /LINE/i.test(sourceMeta) ||
    /LINE/i.test(label)
  ) {
    const base =
      /LINE/i.test(sourceMeta) || /LINE/i.test(label)
        ? "สลิปจาก LINE"
        : label || sourceMeta || "สลิป";
    return transferor ? `${base} · ${transferor}` : base;
  }
  if (item.counterpartLabel?.trim()) return item.counterpartLabel.trim();
  return label;
}

function evidenceDetailAmountLabel(item: EvidenceItem): string | null {
  const fromMinor =
    item.transferAmountMinor ??
    item.matchedAmountMinor ??
    item.outstandingAmountMinor ??
    item.originalAmountMinor ??
    null;
  if (fromMinor != null && Number.isFinite(fromMinor)) {
    return formatAmountMinor(fromMinor, "THB");
  }
  const labeled =
    item.transferAmountLabel?.trim() ||
    item.matchedAmountLabel?.trim() ||
    "";
  if (labeled) return labeled.startsWith("฿") ? labeled : labeled;
  const detailAmt = item.detail?.match(/(฿\s*[\d,.]+)/)?.[1]?.replace(/\s+/g, "");
  return detailAmt || null;
}

/** Expanded 「ดูรายละเอียด」 rows — date + year + (฿amount) like the advice plan line. */
function EvidenceDetailLine({ item }: { item: EvidenceItem }) {
  const title = evidenceDetailTitle(item);
  const date = formatThaiDayMonthYear(item.occurredAt);
  const amount = evidenceDetailAmountLabel(item);

  return (
    <li
      className="flex items-baseline justify-between gap-3 px-3 py-2.5"
      data-testid="fw-evidence-detail-line"
    >
      <span className="min-w-0 truncate text-xs font-medium text-aera-charcoal">
        {title || item.detail || item.label}
      </span>
      <span className="shrink-0 text-right text-xs text-aera-gray">
        {date ? <span>{date} </span> : null}
        {amount ? (
          <span className="font-semibold text-aera-black">({amount})</span>
        ) : null}
      </span>
    </li>
  );
}

function isSlipEvidenceDetailItem(item: EvidenceItem): boolean {
  if (item.transferAmountMinor != null && item.transferAmountMinor > 0) {
    const hay = `${item.label} ${item.sourceLabel ?? ""}`;
    if (/สลิป|LINE|โอน/i.test(hay)) return true;
    if (!isLedgerEvidenceItem(item)) return true;
  }
  const hay = `${item.label} ${item.sourceLabel ?? ""}`;
  return /สลิปจาก LINE|สลิปโอน|LINE OA/i.test(hay);
}

const EVIDENCE_DETAILS_PREVIEW = 4;

function EvidenceDetailsPanel({ items }: { items: EvidenceItem[] }) {
  const [showAll, setShowAll] = useState(false);
  const slipItem = items.find(isSlipEvidenceDetailItem);
  const otherItems = items.filter((item) => item !== slipItem);
  const hiddenCount = Math.max(0, otherItems.length - EVIDENCE_DETAILS_PREVIEW);
  const visibleOthers = showAll
    ? otherItems
    : otherItems.slice(0, EVIDENCE_DETAILS_PREVIEW);
  const slipDate = slipItem
    ? formatThaiDayMonthYear(slipItem.occurredAt)
    : "";
  const slipAmount = slipItem
    ? evidenceDetailAmountLabel(slipItem)
    : null;

  return (
    <div
      className="mt-2 overflow-hidden rounded-xl border border-aera-lightgray/80 bg-aera-white"
      data-testid="fw-evidence-details-panel"
    >
      {slipItem ? (
        <div
          className="border-b border-aera-lightgray/80 bg-aera-offwhite/50 px-3 py-2.5"
          data-testid="fw-evidence-detail-slip"
        >
          <p className="text-[11px] font-semibold text-aera-gray">
            {evidenceDetailTitle(slipItem)}
          </p>
          <p className="mt-0.5 text-xs text-aera-gray">
            {slipDate ? <span>{slipDate} </span> : null}
            {slipAmount ? (
              <span className="font-semibold text-aera-black">
                ({slipAmount})
              </span>
            ) : null}
          </p>
        </div>
      ) : null}
      {visibleOthers.length > 0 ? (
        <ul className="divide-y divide-aera-lightgray/70">
          {visibleOthers.map((item) => (
            <EvidenceDetailLine key={`${item.id}-facts`} item={item} />
          ))}
        </ul>
      ) : null}
      {hiddenCount > 0 ? (
        <button
          type="button"
          className="w-full cursor-pointer border-t border-aera-lightgray/80 bg-aera-warmcream px-4 py-3 text-center text-sm font-semibold text-aera-charcoal transition-colors hover:bg-aera-amber/20"
          data-testid="fw-evidence-details-more"
          aria-expanded={showAll}
          onClick={() => setShowAll((open) => !open)}
        >
          {showAll ? "ย่อรายการ" : `ดูทั้งหมด · อีก ${hiddenCount} รายการ`}
        </button>
      ) : null}
    </div>
  );
}

function EvidenceDetailsSkeleton() {
  return (
    <div
      className="mt-2 space-y-0 overflow-hidden rounded-xl border border-aera-lightgray/80 bg-aera-white"
      data-testid="fw-evidence-details-skeleton"
      aria-busy="true"
      aria-label="กำลังโหลดรายละเอียด"
    >
      <div className="space-y-1.5 border-b border-aera-lightgray/80 bg-aera-offwhite/50 px-3 py-2.5">
        <DetailPulse className="h-3 w-20" />
        <DetailPulse className="h-3.5 w-36" />
      </div>
      {[0, 1, 2].map((row) => (
        <div
          key={row}
          className="flex items-center justify-between gap-3 border-b border-aera-lightgray/70 px-3 py-2.5 last:border-b-0"
        >
          <DetailPulse className="h-3 w-24" />
          <DetailPulse className="h-3 w-28" />
        </div>
      ))}
    </div>
  );
}

const QUEUE_BUCKET_ORDER = [
  "needs_action",
  "waiting",
  "recently_closed",
] as const;

type QueueBucketId = (typeof QUEUE_BUCKET_ORDER)[number];
export type QueueFilterId = "all" | QueueBucketId;

export function parseQueueFilterId(
  raw: string | null | undefined,
): QueueFilterId | null {
  const s = String(raw ?? "").trim();
  if (s === "all") return "all";
  if ((QUEUE_BUCKET_ORDER as readonly string[]).includes(s)) {
    return s as QueueBucketId;
  }
  return null;
}

function FreshnessBanner({
  freshness,
}: {
  freshness: WorkQueueProjection["freshness"] | LiffFinancialWorkCard["freshness"];
}) {
  if (freshness === "current") return null;
  const text =
    freshness === "stale"
      ? "ข้อมูลอาจไม่ใช่ล่าสุด — รีเฟรชเพื่อโหลดสถานะปัจจุบัน"
      : freshness === "projection_pending"
        ? "กำลังอัปเดตสถานะจากเซิร์ฟเวอร์…"
        : "ยังไม่มีคิวงานเงินจากเซิร์ฟเวอร์";
  return (
    <div
      role="status"
      className="rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900"
    >
      {text}
    </div>
  );
}

/** LINE-00: old Flex card opened LIFF with an older `rev` than live projection. */
export function FinancialWorkLineStaleBanner(props: {
  entryRevision: number;
  cardRevision: number;
  onShowCurrent: () => void;
  onReload?: () => void;
}) {
  return (
    <div
      role="status"
      data-testid="fw-line-stale-banner"
      className="rounded-xl border border-amber-200 bg-amber-50 px-3 py-2.5 text-xs text-amber-950"
    >
      <p className="font-medium">
        เปิดจากข้อความ LINE เก่า — กำลังแสดงสถานะล่าสุด
      </p>
      <p className="mt-1 text-amber-900/80">
        ใช้ข้อมูลชุดนี้เท่านั้น ข้อความเก่าในแชทยืนยันผลไม่ได้
      </p>
      <div className="mt-2 flex flex-wrap gap-2">
        <AeraButton
          type="button"
          variant="secondary"
          size="xs"
          className="cursor-pointer"
          data-testid="fw-line-stale-ack"
          onClick={props.onShowCurrent}
        >
          ใช้สถานะปัจจุบัน
        </AeraButton>
        {props.onReload ? (
          <AeraButton
            type="button"
            variant="ghost"
            size="xs"
            className="cursor-pointer"
            data-testid="fw-line-stale-reload"
            onClick={props.onReload}
          >
            รีเฟรชคิว
          </AeraButton>
        ) : null}
      </div>
    </div>
  );
}


export function FinancialWorkOfflineBanner() {
  const { isOnline, canMutate, blockedReason } = useFinancialWorkMutationGate();
  if (isOnline && canMutate) return null;
  return (
    <div
      role="status"
      data-testid="fw-offline-banner"
      className="rounded-xl border border-aera-lightgray bg-aera-offwhite px-3 py-2 text-xs text-aera-charcoal"
    >
      {blockedReason}
    </div>
  );
}

/** Same copy on งานเงิน and ข้อมูล — do not substitute an empty queue or the data table. */
export function FinancialWorkAccessDeniedPanel(props: { onRetry: () => void }) {
  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="shrink-0 px-4 pt-4">
        <FinancialWorkOfflineBanner />
      </div>
      <div
        role="alert"
        className="m-4 flex min-h-0 flex-1 flex-col items-center justify-center rounded-2xl border border-red-200 bg-red-50 px-4 py-8 text-center text-sm text-red-800"
        data-testid="fw-queue-unauthorized"
      >
        <p className="font-semibold text-aera-charcoal">ไม่มีสิทธิ์ดูคิวงานเงิน</p>
        <p className="mt-2 text-xs text-aera-gray">
          ระบบไม่สร้างรายการคิวแทนเมื่อไม่มีสิทธิ์
        </p>
        <AeraButton
          variant="secondary"
          size="xs"
          className="mt-3 cursor-pointer"
          onClick={props.onRetry}
        >
          ลองใหม่
        </AeraButton>
      </div>
    </div>
  );
}

/** Same in-card row as กำลังเตรียมข้อมูล — keep page chrome, replace the list. */
export function FinancialWorkLoadFailedPanel(props: {
  onRetry: () => void;
  title?: string;
  description?: string;
}) {
  return (
    <div
      className="flex min-h-0 flex-1 items-center justify-start px-4 py-4"
      role="alert"
      data-testid="fw-data-load-failed"
    >
      <FinancialWorkPreparingStatus
        variant="failed"
        title={props.title ?? "โหลดข้อมูลไม่สำเร็จ"}
        description={
          props.description ??
          "ข้อมูลชุดนี้ยังไม่พร้อมแสดง ลองโหลดใหม่อีกครั้ง"
        }
        action={
          <AeraButton
            type="button"
            variant="transparent"
            // size="xs"
            // paddingSize="none"
            textColor="gray"
            fontWeight="medium"
            className="cursor-pointer"
            onClick={props.onRetry}
          >
            ลองใหม่
          </AeraButton>
        }
      />
    </div>
  );
}

export function FinancialWorkPrimaryNav(props: {
  active: FinancialWorkNavId;
  onChange: (nav: FinancialWorkNavId) => void;
  showPeriods?: boolean;
  showSources?: boolean;
  workCount?: number | null;
  workCountLoading?: boolean;
  variant?: "top" | "bottom" | "sidebar";
}) {
  const variant = props.variant ?? "top";
  const workCount =
    typeof props.workCount === "number" ? props.workCount : null;
  const countKnown = workCount != null && workCount > 0;
  const workCountLabel = (workCount ?? 0).toLocaleString("th-TH");
  const workCountBadge = countKnown ? (
    <span
      data-testid="fw-work-count"
      className="inline-flex h-5 min-w-5 shrink-0 items-center justify-center rounded-full px-1.5 text-[11px] font-bold tabular-nums transition-colors duration-200 ease-[cubic-bezier(0.16,1,0.3,1)] motion-reduce:transition-none bg-aera-warmcream text-aera-amberdark"
    >
      {workCountLabel}
    </span>
  ) : props.workCountLoading ? (
    <span
      data-testid="fw-work-count"
      data-loading="true"
      className="inline-flex h-5 min-w-5 shrink-0 animate-pulse rounded-full bg-aera-amber/35"
      aria-hidden
    />
  ) : null;
  const tabs = useMemo(() => {
    const items: SegmentedTabItem<FinancialWorkNavId>[] = [
      {
        id: "work",
        label: "งานเงิน",
        icon: CircleAlert,
        trailing: variant === "top" ? workCountBadge : undefined,
      },
      {
        id: "data",
        label: "ข้อมูล",
        icon: Search,
      },
    ];
    if (props.showSources) {
      items.push({
        id: "sources",
        label: "แหล่งข้อมูล/การแชร์",
        icon: Layers,
      });
    }
    if (props.showPeriods) {
      items.push({ id: "periods", label: "รอบบัญชี", icon: CalendarRange });
    }
    return items;
  }, [props.showPeriods, props.showSources, variant, workCount, workCountBadge]);

  if (variant === "sidebar") {
    const navTabs = tabs.filter(
      (tab) => tab.id === "work" || tab.id === "data" || tab.id === "sources",
    );
    const activeIndex = navTabs.findIndex((tab) => tab.id === props.active);
    const itemSizePx = 40;
    const itemGapPx = 4;

    return (
      <nav
        className="flex flex-col gap-1"
        data-testid="fw-primary-nav"
        aria-label="เมนูงานเงิน"
      >
        <p className="px-4 pb-1 text-xs font-medium text-aera-gray">งานหลัก</p>
        <div className="relative flex flex-col gap-1">
          {activeIndex >= 0 ? (
            <div
              aria-hidden
              className="pointer-events-none absolute inset-y-0 left-0 z-20"
            >
              <div
                className="absolute left-0 top-1 h-8 w-[3px] rounded-full bg-aera-amber transition-transform duration-300 ease-[cubic-bezier(0.16,1,0.3,1)] motion-reduce:transition-none"
                style={{
                  transform: `translateY(${activeIndex * (itemSizePx + itemGapPx)}px)`,
                }}
              />
            </div>
          ) : null}
          {navTabs.map((tab) => {
            const isActive = props.active === tab.id;
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                type="button"
                role="tab"
                aria-selected={isActive}
                aria-label={
                  tab.id === "work"
                    ? countKnown
                      ? `${tab.label} ${workCountLabel}`
                      : tab.label
                    : tab.label
                }
                onClick={() => props.onChange(tab.id)}
                className={`group relative z-10 flex h-10 cursor-pointer items-center gap-2 px-4 text-sm transition-colors duration-200 ease-[cubic-bezier(0.16,1,0.3,1)] motion-reduce:transition-none ${
                  isActive
                    ? "font-semibold text-aera-amber"
                    : "font-medium text-aera-gray hover:text-aera-charcoal"
                }`}
              >
                {Icon ? (
                  <span
                    className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-lg transition-colors duration-200 ease-[cubic-bezier(0.16,1,0.3,1)] motion-reduce:transition-none ${
                      isActive ? "bg-aera-lightgray" : "bg-transparent"
                    }`}
                  >
                    <Icon
                      className={`h-4 w-4 transition-colors duration-200 ease-[cubic-bezier(0.16,1,0.3,1)] motion-reduce:transition-none ${
                        isActive ? "text-aera-amber" : ""
                      }`}
                      aria-hidden
                    />
                  </span>
                ) : null}
                <span className="min-w-0 flex-1 truncate text-left">
                  {tab.label}
                </span>
                {tab.id === "work" ? workCountBadge : null}
              </button>
            );
          })}
        </div>
      </nav>
    );
  }

  if (variant === "bottom") {
    const navTabs = tabs.filter(
      (tab) => tab.id === "work" || tab.id === "data" || tab.id === "sources",
    );
    const activeIndex = navTabs.findIndex((tab) => tab.id === props.active);
    const colCount = Math.max(navTabs.length, 1);

    return (
      <nav
        className="fixed inset-x-0 bottom-0 z-40 border-t border-aera-lightgray bg-aera-white pb-[max(0.25rem,env(safe-area-inset-bottom))] min-[900px]:hidden"
        data-testid="fw-primary-nav"
        aria-label="เมนูงานเงิน"
      >
        <div
          className={`relative grid min-h-14 ${colCount === 3 ? "grid-cols-3" : "grid-cols-2"}`}
        >
          {activeIndex >= 0 ? (
            <div
              aria-hidden
              className="pointer-events-none absolute inset-x-0 top-0 z-20 h-0.5"
            >
              <div
                className="h-0.5 bg-aera-amber transition-transform duration-300 ease-[cubic-bezier(0.16,1,0.3,1)] motion-reduce:transition-none"
                style={{
                  width: `${100 / colCount}%`,
                  transform: `translateX(${activeIndex * 100}%)`,
                }}
              />
            </div>
          ) : null}
          {navTabs.map((tab) => {
            const isActive = props.active === tab.id;
            const Icon = tab.icon;
            const isWorkTab = tab.id === "work";
            return (
              <button
                key={tab.id}
                type="button"
                role="tab"
                aria-selected={isActive}
                aria-label={isWorkTab ? (countKnown ? `${tab.label} ${workCountLabel}` : tab.label) : tab.label}
                onClick={() => props.onChange(tab.id)}
                className={`group relative z-10 flex min-w-0 cursor-pointer items-center justify-center gap-2 px-4 py-3 text-sm transition-colors duration-200 ease-[cubic-bezier(0.16,1,0.3,1)] motion-reduce:transition-none ${
                  isActive
                    ? "font-semibold text-aera-charcoal"
                    : "font-medium text-aera-gray"
                }`}
              >
                {Icon ? (
                  isWorkTab ? (
                    <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-aera-lightgray">
                      <Icon className="h-4 w-4 text-aera-charcoal" aria-hidden />
                    </span>
                  ) : (
                    <Icon
                      className={`h-4 w-4 shrink-0 ${
                        isActive ? "text-aera-charcoal" : "text-aera-gray"
                      }`}
                      aria-hidden
                    />
                  )
                ) : null}
                <span className="truncate">{tab.label}</span>
                {isWorkTab ? workCountBadge : null}
              </button>
            );
          })}
        </div>
      </nav>
    );
  }

  return (
    <div
      className="flex h-full min-w-0 items-stretch px-5 pt-2.5"
      data-testid="fw-primary-nav"
    >
      <SegmentedTabSwitcher
        tabs={tabs.filter(
          (tab) => tab.id === "work" || tab.id === "data" || tab.id === "sources",
        )}
        activeTab={props.active}
        onChange={props.onChange}
        ariaLabel="เมนูงานเงิน"
        className="h-full"
      />
    </div>
  );
}

function WorkCard(props: {
  card: LiffFinancialWorkCard;
  onOpen: (card: LiffFinancialWorkCard) => void;
  onAction: (action: ActionDirective, card: LiffFinancialWorkCard) => void;
  canMutate: boolean;
}) {
  const { card } = props;
  const isBatch = isReceivableBatchCard(card);
  const fromBatch = isFromBatchCard(card);
  const isAsk = isSlipOwnerAskCard(card);
  const allChildren = card.backlogChildren ?? card.outstanding?.items ?? [];
  const memberCount =
    allChildren.length || card.outstanding?.openCount || 0;
  const preview = allChildren.slice(0, 4);
  const extraCount = Math.max(0, allChildren.length - preview.length);
  const amount = card.amountSummary
    ? formatAmountMinor(
        card.amountSummary.primaryAmountMinor,
        card.amountSummary.currency,
      )
    : null;
  const dataAction = card.availableActions.find((a) => a.actionType === "open_data");
  const moneyAction = isAsk
    ? undefined
    : card.availableActions.find(
        (a) =>
          a.actionType !== "open_detail" &&
          a.actionType !== "open_outstanding" &&
          a.actionType !== "open_data",
      );
  const askPreview = isAsk ? card.consequenceRows.slice(0, 4) : [];
  const askExtra = isAsk
    ? Math.max(0, card.consequenceRows.length - askPreview.length)
    : 0;

  const statusLabel = workStatusLabel(card);
  const showSubtitle =
    Boolean(card.subtitle) &&
    card.subtitle !== statusLabel &&
    card.subtitle !== surfaceStateLabel(card.surfaceState) &&
    !isBatch;
  const showNext =
    !isAsk &&
    Boolean(card.nextStep.label) &&
    !card.nextStep.label.includes(statusLabel) &&
    card.nextStep.label !== "ไม่ต้องทำอะไรเพิ่ม" &&
    !isBatch;

  return (
    <article
      data-testid="fw-work-card"
      data-object-id={card.objectRef.id}
      data-card-kind={isBatch ? "batch" : "single"}
      className={[
        "rounded-2xl border bg-aera-white p-3.5 shadow-[0_1px_0_rgba(0,0,0,0.03)]",
        isBatch
          ? "border-aera-amber/30 border-l-[3px] border-l-aera-amber"
          : "border-aera-lightgray/80",
      ].join(" ")}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-1.5">
            <span
              data-testid={
                isBatch
                  ? "fw-card-kind-batch"
                  : fromBatch
                    ? "fw-card-kind-from-batch"
                    : "fw-card-kind-single"
              }
              className={[
                "rounded-full px-2 py-0.5 text-[10px] font-semibold tracking-wide",
                isBatch || fromBatch
                  ? "bg-aera-amber text-aera-charcoal"
                  : "bg-aera-warmcream text-aera-amberdark",
              ].join(" ")}
            >
              {isBatch
                ? `ชุด · ${memberCount} ราย`
                : fromBatch
                  ? "จากชุด"
                  : "เดี่ยว"}
            </span>
            <p className="text-[11px] font-medium text-aera-gray">{statusLabel}</p>
          </div>
          <h3 className="mt-1 break-words text-sm font-semibold text-aera-charcoal">
            {displayWorkTitle(card.title)}
          </h3>
          {showSubtitle ? (
            <p className="mt-0.5 break-words text-xs text-aera-gray">{card.subtitle}</p>
          ) : null}
          {!isBatch && card.outstanding ? (
            <p className="mt-1 text-[11px] text-aera-gray">
              ยังค้าง {card.outstanding.openCount} ราย
              {card.outstanding.remainingAmountMinor != null
                ? ` · ${formatAmountMinor(
                    card.outstanding.remainingAmountMinor,
                    card.outstanding.currency || "THB",
                  )}`
                : ""}
            </p>
          ) : null}
        </div>
        {amount ? (
          <p className="shrink-0 text-sm font-semibold tabular-nums text-aera-charcoal">
            {amount}
          </p>
        ) : null}
      </div>

      {isAsk && askPreview.length > 0 ? (
        <ol
          data-testid="fw-ask-options"
          className="mt-2.5 divide-y divide-aera-lightgray/70 rounded-xl border border-aera-amber/20 bg-aera-warmcream/40"
        >
          {askPreview.map((row, index) => (
            <li
              key={`${row.objectRef.kind}:${row.objectRef.id}`}
              className="flex items-baseline justify-between gap-3 px-2.5 py-1.5 text-xs"
            >
              <span className="min-w-0 truncate text-aera-charcoal">
                {index + 1}. {row.label}
              </span>
              {row.amountMinor != null ? (
                <span className="shrink-0 tabular-nums text-aera-gray">
                  {formatAmountMinor(row.amountMinor, row.currency || "THB")}
                </span>
              ) : null}
            </li>
          ))}
          {askExtra > 0 ? (
            <li className="px-2.5 py-1.5 text-[11px] text-aera-gray">
              + อีก {askExtra} รายการ
            </li>
          ) : null}
        </ol>
      ) : null}

      {isBatch && preview.length > 0 ? (
        <ul
          data-testid="fw-batch-children"
          className="mt-2.5 divide-y divide-aera-lightgray/70 rounded-xl border border-aera-lightgray/70 bg-aera-offwhite/60"
        >
          {preview.map((row) => (
            <li
              key={`${row.objectRef.kind}:${row.objectRef.id}`}
              className="flex items-baseline justify-between gap-3 px-2.5 py-1.5 text-xs"
            >
              <span className="min-w-0 truncate text-aera-charcoal">{row.label}</span>
              <span className="shrink-0 tabular-nums text-aera-gray">
                {formatAmountMinor(
                  row.status === "paid" ? 0 : row.remainingAmountMinor ?? 0,
                  row.currency || "THB",
                )}
              </span>
            </li>
          ))}
          {extraCount > 0 ? (
            <li className="px-2.5 py-1.5 text-[11px] text-aera-gray">
              + อีก {extraCount} รายการ
            </li>
          ) : null}
        </ul>
      ) : null}

      {showNext ? (
        <p className="mt-2 text-xs text-aera-gray">{card.nextStep.label}</p>
      ) : null}

      <div className="mt-3 flex flex-wrap gap-2">
        <AeraButton
          type="button"
          variant="secondary"
          size="xs"
          data-testid="fw-open-detail"
          onClick={() => props.onOpen(card)}
        >
          ดูรายละเอียด
        </AeraButton>
        {dataAction ? (
          <AeraButton
            type="button"
            variant="outline"
            size="xs"
            data-testid="fw-open-data"
            disabled={!props.canMutate}
            onClick={() => props.onAction(dataAction, card)}
            title={!props.canMutate ? OFFLINE_HINT : undefined}
          >
            {dataAction.label || "แก้ไขยอด"}
          </AeraButton>
        ) : null}
        {moneyAction ? (
          <AeraButton
            type="button"
            variant="primary"
            size="xs"
            disabled={!props.canMutate}
            onClick={() => props.onAction(moneyAction, card)}
            title={!props.canMutate ? OFFLINE_HINT : undefined}
          >
            {moneyAction.label || "ทำรายการ"}
          </AeraButton>
        ) : null}
      </div>
    </article>
  );
}

function QueueGroupCollapse(props: {
  testId: string;
  label: string;
  count: number;
  variant: "batch" | "single";
  children: ReactNode;
}) {
  const [open, setOpen] = useState(true);
  const panelId = `${props.testId}-panel`;
  const isBatch = props.variant === "batch";
  const Icon = isBatch ? Layers : User;
  return (
    <div
      className={[
        "overflow-hidden rounded-2xl border bg-aera-white shadow-[0_1px_0_rgba(0,0,0,0.03)] transition-colors duration-200",
        open ? "border-aera-amber/50" : "border-aera-lightgray hover:border-aera-amber/40",
      ].join(" ")}
      data-testid={props.testId}
      data-collapsed={open ? "false" : "true"}
    >
      <button
        type="button"
        className="flex min-h-12 w-full cursor-pointer items-center gap-3 px-3 py-2.5 text-left transition-colors duration-200 hover:bg-aera-warmcream/50"
        aria-expanded={open}
        aria-controls={panelId}
        data-testid={`${props.testId}-toggle`}
        onClick={() => setOpen((value) => !value)}
      >
        <span
          className={[
            "flex h-9 w-9 shrink-0 items-center justify-center rounded-xl",
            isBatch
              ? "bg-aera-amber text-aera-charcoal"
              : "bg-aera-warmcream text-aera-amberdark",
          ].join(" ")}
        >
          <Icon size={16} strokeWidth={2.2} aria-hidden />
        </span>
        <span className="min-w-0 flex-1">
          <span className="block text-sm font-semibold text-aera-charcoal">
            {props.label}
          </span>
          <span className="mt-0.5 block text-xs text-aera-gray">
            {open
              ? isBatch
                ? "ชุดที่ประกาศพร้อมกัน"
                : "ยอดที่ประกาศทีละรายการ"
              : `มี ${props.count} รายการ · กดเพื่อดู`}
          </span>
        </span>
        <span className="rounded-full bg-aera-amber/15 px-2 py-0.5 text-xs font-semibold tabular-nums text-aera-amberdark">
          {props.count}
        </span>
        <ChevronDown
          size={18}
          strokeWidth={2.2}
          aria-hidden
          className={[
            "shrink-0 text-aera-amber transition-transform duration-300 ease-out",
            open ? "rotate-0" : "-rotate-90",
          ].join(" ")}
        />
      </button>
      <div
        id={panelId}
        role="region"
        aria-label={props.label}
        aria-hidden={!open}
        className={[
          "grid transition-[grid-template-rows,opacity] duration-300 ease-out",
          open ? "grid-rows-[auto] opacity-100" : "grid-rows-[0fr] opacity-0",
        ].join(" ")}
      >
        <div className="min-h-0 overflow-hidden">
          <div className="space-y-2.5 border-t border-aera-amber/20 bg-aera-warmcream/40 px-2.5 py-2.5">
            {props.children}
          </div>
        </div>
      </div>
    </div>
  );
}

function QueueBucketCardList(props: {
  cards: LiffFinancialWorkCard[];
  onOpen: (card: LiffFinancialWorkCard) => void;
  onAction: (action: ActionDirective, card: LiffFinancialWorkCard) => void;
  canMutate: boolean;
}) {
  const { batches, fromBatch, singles } = partitionWorkCards(props.cards);
  const renderCards = (cards: LiffFinancialWorkCard[]) =>
    cards.map((card) => (
      <WorkCard
        key={`${card.objectRef.kind}:${card.objectRef.id}:${card.projectionRevision}`}
        card={card}
        onOpen={props.onOpen}
        onAction={props.onAction}
        canMutate={props.canMutate}
      />
    ));
  return (
    <div className="space-y-3">
      {batches.length > 0 ? (
        <QueueGroupCollapse
          testId="fw-queue-batch-group"
          label="ชุดยอดค้าง"
          count={batches.length}
          variant="batch"
        >
          {renderCards(batches)}
        </QueueGroupCollapse>
      ) : null}
      {fromBatch.length > 0 ? (
        <QueueGroupCollapse
          testId="fw-queue-from-batch-group"
          label="จากชุด"
          count={fromBatch.length}
          variant="batch"
        >
          {renderCards(fromBatch)}
        </QueueGroupCollapse>
      ) : null}
      {singles.length > 0 ? (
        <QueueGroupCollapse
          testId="fw-queue-single-group"
          label="ยอดเดี่ยว"
          count={singles.length}
          variant="single"
        >
          {renderCards(singles)}
        </QueueGroupCollapse>
      ) : null}
    </div>
  );
}

const OFFLINE_HINT = "ออฟไลน์ — ยังทำรายการเงินไม่ได้";

/** In-page preparing for งานเงิน after the workspace has already loaded (tab switch). */
export function FinancialWorkQueuePreparing(props: {
  workspaceId: string | null;
  workspaceName?: string | null;
  serviceId?: string | null;
  failed?: boolean;
  onRetry?: () => void;
}) {
  const isWideLayout = useWideDataLayout();
  const fillShellHeight = useFinancialWorkWideRail();
  const fillTableHeight = isWideLayout;
  const failed = Boolean(props.failed);
  return (
    <div
      className={
        fillShellHeight
          ? "flex min-h-0 min-w-0 flex-1 flex-col"
          : fillTableHeight
            ? "flex min-h-full min-w-0 flex-1 flex-col"
            : "flex shrink-0 flex-col gap-5"
      }
      data-testid={failed ? "fw-queue-error" : "fw-queue-preparing"}
      role={failed ? "alert" : "status"}
      aria-live="polite"
      aria-busy={!failed}
      aria-label={failed ? "โหลดข้อมูลไม่สำเร็จ" : "กำลังเตรียมข้อมูล"}
    >
      <div
        className={
          isWideLayout
            ? "shrink-0 px-4 pb-4 pt-4 min-[900px]:px-6 min-[900px]:pt-5"
            : "shrink-0"
        }
      >
        <FinancialWorkSectionHeader
          wide={isWideLayout}
          eyebrow="ภาพรวมการเงิน"
          eyebrowIcon={<Sparkles className="h-3.5 w-3.5" aria-hidden />}
          title="งานเงิน"
          description="ดูเงินที่เกิดขึ้นแล้ว และจัดการเฉพาะเรื่องที่ยังต้องใช้คำตอบจากคุณ"
          status={
            <DataSourcesStatusControl
              workspaceId={props.workspaceId}
              workspaceName={props.workspaceName}
              serviceId={props.serviceId}
              size={isWideLayout ? "sm" : "md"}
            />
          }
        />
      </div>
      <section
        className={
          fillTableHeight
            ? "mx-4 mb-4 flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden rounded-2xl border border-aera-lightgray bg-aera-white min-[900px]:mx-6 min-[900px]:mb-6"
            : "flex min-h-[12rem] shrink-0 flex-col overflow-visible rounded-2xl border border-aera-lightgray bg-aera-white"
        }
      >
        {failed ? (
          <FinancialWorkLoadFailedPanel onRetry={props.onRetry ?? (() => {})} />
        ) : (
          <div className="flex min-h-0 flex-1 items-center justify-start px-4 py-20">
            <FinancialWorkPreparingStatus
              title="กำลังเตรียมข้อมูล"
              description="Bill-Lin กำลังรวบรวมรายการล่าสุดให้คุณ"
            />
          </div>
        )}
      </section>
    </div>
  );
}

export function FinancialWorkQueueView(props: {
  projection: WorkQueueProjection | null;
  loading: boolean;
  countsLoading?: boolean;
  error: string | null;
  errorKind?: QueueErrorKind | null;
  workspaceId?: string | null;
  workspaceName?: string | null;
  serviceId?: string | null;
  ownerUserId?: string | null;
  /** Restored from URL `fwFilter` after detail close. */
  initialFilter?: QueueFilterId;
  onFilterChange?: (filter: QueueFilterId) => void;
  pageIndex?: number;
  onPageChange?: (page: number) => void;
  pageLoading?: boolean;
  onPageRetry?: () => void;
  pageError?: string | null;
  onOpenCard: (card: LiffFinancialWorkCard) => void;
  onAction: (action: ActionDirective, card: LiffFinancialWorkCard) => void;
  onOpenData: () => void;
  onRetry: () => void;
}) {
  const { projection, loading, error } = props;
  const [filter, setFilter] = useState<QueueFilterId>(
    props.initialFilter ?? "needs_action",
  );

  useEffect(() => {
    if (props.initialFilter && props.initialFilter !== filter) {
      setFilter(props.initialFilter);
    }
    // Sync only when URL-driven initialFilter changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.initialFilter]);

  const setFilterAndNotify = (next: QueueFilterId) => {
    setFilter(next);
    props.onFilterChange?.(next);
  };

  if ((loading && !projection) || (props.errorKind === "unauthorized" && loading)) {
    return (
      <FinancialWorkQueuePreparing
        workspaceId={props.workspaceId ?? null}
        workspaceName={props.workspaceName}
        serviceId={props.serviceId}
      />
    );
  }

  if (error) {
    if (props.errorKind === "unauthorized") {
      return <FinancialWorkAccessDeniedPanel onRetry={props.onRetry} />;
    }
    return (
      <FinancialWorkQueuePreparing
        workspaceId={props.workspaceId ?? null}
        workspaceName={props.workspaceName}
        serviceId={props.serviceId}
        failed
        onRetry={props.onRetry}
      />
    );
  }

  if (!projection || projection.serverProjectionMissing || projection.freshness === "unavailable") {
    return (
      <div
        className="flex min-h-0 flex-1 flex-col items-center justify-center px-4 py-6 text-center min-[900px]:px-6"
        data-testid="fw-queue-unavailable"
      >
        <div className="mx-auto w-full max-w-sm space-y-3">
          <p className="text-base font-semibold text-aera-charcoal">
            ยังไม่มีคิวงานเงินจากเซิร์ฟเวอร์
          </p>
          <p className="text-sm text-aera-gray">
            คิวงานเงินดึงจากเซิร์ฟเวอร์ของร้านนี้ — ถ้ายังว่าง ให้เปิดแท็บข้อมูลดูยอดค้างตามเดิม
          </p>
          <div className="flex flex-wrap items-center justify-center gap-2 pt-1">
            <AeraButton
              variant="primary"
              size="sm"
              className="cursor-pointer"
              onClick={props.onOpenData}
            >
              ไปที่ข้อมูล
            </AeraButton>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-full min-w-0 flex-1 flex-col">
      <div className="shrink-0 empty:hidden px-4 pt-4 min-[900px]:px-6 min-[900px]:pt-5">
        <FreshnessBanner freshness={projection.freshness} />
        <FinancialWorkOfflineBanner />
      </div>
      <FinancialWorkDesktopQueue
        projection={projection}
        workspaceId={props.workspaceId ?? null}
        workspaceName={props.workspaceName}
        serviceId={props.serviceId}
        ownerUserId={props.ownerUserId}
        filter={filter}
        onFilterChange={setFilterAndNotify}
        onOpenCard={props.onOpenCard}
        pageIndex={props.pageIndex}
        onPageChange={props.onPageChange}
        pageLoading={props.pageLoading}
        onPageRetry={props.onPageRetry}
        pageError={props.pageError}
        countsLoading={props.countsLoading ?? props.loading}
      />
    </div>
  );
}

export function FinancialWorkQueueSkeleton() {
  return (
    <div
      className="flex h-full min-h-0 flex-1 flex-col items-center justify-center"
      aria-busy="true"
      aria-label="กำลังโหลด"
      data-testid="fw-queue-skeleton"
    >
      <FinancialWorkPreparingStatus />
    </div>
  );
}

function DetailPulse({ className }: { className: string }) {
  return (
    <div
      className={["animate-pulse rounded-md bg-aera-lightgray/80", className].join(
        " ",
      )}
      aria-hidden
    />
  );
}

/** Matches the Bill-Lin advice card chrome (avatar · title · plan · book footer). */
function FinancialWorkAdviceCardSkeleton(props?: { testId?: string }) {
  return (
    <section
      className="rounded-2xl border border-aera-lightgray bg-aera-white px-4 py-3.5"
      data-testid={props?.testId ?? "fw-advice-card-skeleton"}
      aria-hidden
    >
      <div className="flex items-center gap-2">
        <DetailPulse className="h-8 w-8 shrink-0 rounded-full" />
        <DetailPulse className="h-3 w-36 max-w-[55%] flex-1" />
        <DetailPulse className="h-3 w-10 shrink-0" />
      </div>
      <DetailPulse className="mt-2.5 h-4 w-28" />
      <DetailPulse className="mt-2 h-3 w-52 max-w-full" />
      <div className="mt-3 flex items-center justify-between gap-3 border-t border-aera-lightgray pt-2.5">
        <DetailPulse className="h-3 w-16" />
        <DetailPulse className="h-3.5 w-32 shrink-0" />
      </div>
    </section>
  );
}

/** Placeholder that matches the detail drawer while the work-queue projection loads. */
export function FinancialWorkDetailSkeleton(props: {
  embeddedInModal?: boolean;
}) {
  const embedded = props.embeddedInModal ?? false;
  return (
    <div
      role="status"
      aria-busy="true"
      aria-label="กำลังโหลดรายละเอียดงานเงิน"
      data-testid="fw-detail-skeleton"
      className={
        embedded
          ? "space-y-3"
          : "space-y-3 pb-[calc(5.5rem+env(safe-area-inset-bottom,0px))]"
      }
    >
      <section className="rounded-2xl border border-aera-lightgray/80 bg-aera-white px-4 py-4">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0 flex-1 space-y-2.5">
            <DetailPulse className="h-5 w-16 rounded-full" />
            <DetailPulse className="h-8 w-36" />
            <DetailPulse className="h-4 w-24" />
          </div>
          <DetailPulse className="h-6 w-16 shrink-0 rounded-full" />
        </div>
        <div className="mt-4 grid grid-cols-3 gap-2">
          <DetailPulse className="h-14 rounded-xl" />
          <DetailPulse className="h-14 rounded-xl" />
          <DetailPulse className="h-14 rounded-xl" />
        </div>
      </section>
      <FinancialWorkAdviceCardSkeleton />
      <section className="overflow-hidden rounded-2xl border border-aera-lightgray/80 bg-aera-white">
        <div className="border-b border-aera-lightgray/70 px-4 py-3">
          <DetailPulse className="h-3 w-20" />
        </div>
        {[0, 1, 2].map((row) => (
          <div
            key={row}
            className="flex items-center gap-2 border-b border-aera-lightgray/70 px-3 py-2.5 last:border-b-0"
          >
            <DetailPulse className="h-9 w-9 shrink-0 rounded-full" />
            <div className="min-w-0 flex-1 space-y-1.5">
              <DetailPulse className="h-3.5 w-24 max-w-[50%]" />
              <DetailPulse className="h-2.5 w-16" />
            </div>
            <DetailPulse className="h-3.5 w-14 shrink-0" />
          </div>
        ))}
      </section>
      <section className="rounded-2xl border border-aera-lightgray/80 bg-aera-white px-4 py-3.5">
        <DetailPulse className="h-3 w-16" />
        <DetailPulse className="mt-3 h-16 w-full rounded-xl" />
      </section>
    </div>
  );
}

function QueueFilterChip(props: {
  selected: boolean;
  label: string;
  count: number;
  muted?: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={props.selected}
      onClick={props.onClick}
      className={[
        "inline-flex shrink-0 cursor-pointer items-center gap-1 rounded-full border px-2.5 py-1 text-xs font-medium transition-colors",
        props.selected
          ? "border-aera-amber bg-aera-amber text-aera-charcoal"
          : props.muted
            ? "border-aera-lightgray/70 bg-aera-white text-aera-gray"
            : "border-aera-lightgray bg-aera-white text-aera-charcoal hover:border-aera-amber/50",
      ].join(" ")}
    >
      <span>{props.label}</span>
      <span
        className={[
          "tabular-nums",
          props.selected ? "text-aera-charcoal/70" : "text-aera-gray",
        ].join(" ")}
      >
        {props.count}
      </span>
    </button>
  );
}

function detailAmountCaption(card: LiffFinancialWorkCard, closed: boolean): string {
  if (closed && card.amountSummary?.basis === "cash") return "เงินสดที่บันทึก";
  if (card.amountSummary?.basis === "cash") return "ยอดที่กำลังตรวจ";
  if (card.amountSummary?.basis === "gross") return "ยอดเอกสาร";
  return (
    amountBasisLabel(card.amountSummary?.basis, { complete: closed }) ||
    "ยอดที่เกี่ยวข้อง"
  );
}

function detailBalanceStatus(card: LiffFinancialWorkCard, closed: boolean): string {
  if (closed) return "บันทึกแล้ว";
  if (card.surfaceState === "risk") return "ยังไม่จ่าย";
  return "รอการตัดสินใจ";
}

function detailSourceLabel(
  card: LiffFinancialWorkCard,
  evidenceItems?: EvidenceItem[],
  incomeRecord?: IncomeRecord | null,
): string {
  const fromProvenance = closedProvenanceSourceLabel(
    card,
    incomeRecord,
    evidenceItems,
  );
  if (fromProvenance !== "—") return fromProvenance;
  return "—";
}

function isNavOnlyAction(action: ActionDirective): boolean {
  return (
    action.actionType === "open_detail" ||
    action.actionType === "open_outstanding" ||
    action.actionType === "open_data"
  );
}

function askOptionKey(ref: { kind: string; id: string }): string {
  return `${ref.kind}:${ref.id}`;
}

function askOptionDisplayName(label: string): string {
  const trimmed = label.trim();
  const withoutAmount = trimmed.replace(/\s*\([^)]*บาท\)\s*$/, "").trim();
  return withoutAmount || trimmed || "ยอดนี้";
}

function isWaitingReceivableCard(
  card: LiffFinancialWorkCard | null | undefined,
): boolean {
  if (!card || card.objectRef.kind !== "receivable") return false;
  if (card.requirementStatus?.complete) return false;
  return (
    card.workPlacement?.bucket === "waiting" ||
    card.surfaceState === "waiting"
  );
}

/**
 * Match API expects Redis announcement order id (`source_ref`), not the PG
 * receivable id that financial-work cards put on `objectRef.id`.
 */
function receivableOrderIdForMatch(
  card: LiffFinancialWorkCard,
  announcements: ReceivableListItem[] = [],
): string | null {
  const receivableId = card.objectRef.id?.trim();
  if (!receivableId) return null;

  const fromList = announcements.find(
    (item) => item.receivableId?.trim() === receivableId,
  );
  const fromListOrderId = fromList ? getReceivableOrderId(fromList) : null;
  if (fromListOrderId) return fromListOrderId;

  const navId = card.availableActions
    .find(
      (action) =>
        action.actionType === "open_data" &&
        action.dataNav?.workerId === "bill-lin-receivable",
    )
    ?.dataNav?.recordId?.trim();
  if (navId && navId !== receivableId) return navId;

  const outstandingNav = card.outstanding?.items
    ?.find((row) => row.objectRef.id === receivableId)
    ?.dataNav?.recordId?.trim();
  if (outstandingNav && outstandingNav !== receivableId) return outstandingNav;

  if (receivableId.startsWith("redis:")) return receivableId.slice(6);
  return receivableId;
}

function parsePendingSlipAmountMinor(
  raw: string | null | undefined,
): number | null {
  if (!raw || raw === "—") return null;
  const n = Number(String(raw).replace(/[^\d.-]/g, ""));
  return Number.isFinite(n) ? Math.round(n * 100) : null;
}

function pendingSlipFieldAmountMinor(
  item: ReceivableListItem,
  keys: readonly string[],
): number | null {
  for (const key of keys) {
    const parsed = parsePendingSlipAmountMinor(
      item.fields.find((field) => field.key === key)?.value?.trim(),
    );
    if (parsed != null) return parsed;
  }
  return null;
}

function pendingSlipOriginalAmountMinor(item: ReceivableListItem): number | null {
  return (
    pendingSlipFieldAmountMinor(item, ["amount_original"]) ??
    parsePendingSlipAmountMinor(item.amountLabel?.trim())
  );
}

function pendingSlipRemainingAmountMinor(item: ReceivableListItem): number | null {
  const original = pendingSlipOriginalAmountMinor(item);
  const allocated = pendingSlipFieldAmountMinor(item, [
    "amount_allocated",
    "allocated_amount",
  ]);
  if (original != null && allocated != null && allocated > 0) {
    return Math.max(0, original - allocated);
  }
  return pendingSlipFieldAmountMinor(item, [
    "amount_remaining",
    "slip_amount",
    "unallocated_amount",
    "amount",
  ]);
}

function pendingSlipOptionLabel(item: ReceivableListItem): string {
  const sender =
    item.fields.find((field) => field.key === "slip_sender")?.value?.trim() ||
    item.fields.find((field) => field.key === "customer_ref")?.value?.trim() ||
    "";
  const title = item.title?.trim() || "";
  const name = sender || title || "สลิปค้าง";
  const originalMinor = pendingSlipOriginalAmountMinor(item);
  if (originalMinor != null) {
    const bare = formatAmountMinor(originalMinor, "THB").replace(/^฿\s*/, "");
    return `${name} (${bare} บาท)`;
  }
  const amount = item.amountLabel?.trim();
  if (amount && amount !== "—") {
    const bare = amount.replace(/^฿\s*/, "");
    return `${name} (${bare} บาท)`;
  }
  return name;
}

/** Stable unique option id — `item.key` can collide (e.g. name:gg for two slips). */
function pendingSlipOptionId(item: ReceivableListItem, index: number): string {
  const orderId = getReceivableOrderId(item);
  if (orderId) return `order:${orderId}`;
  const paymentId = item.paymentId?.trim();
  if (paymentId) return `payment:${paymentId}`;
  const mergeKey = item.mergeKey?.trim();
  if (mergeKey) return `merge:${mergeKey}`;
  const key = item.key?.trim();
  if (key) return `key:${key}#${index}`;
  return `slip#${index}`;
}

function uniquePendingSlipItems(
  items: ReceivableListItem[],
): ReceivableListItem[] {
  const seen = new Set<string>();
  const out: ReceivableListItem[] = [];
  items.forEach((item, index) => {
    const id = pendingSlipOptionId(item, index);
    if (seen.has(id)) return;
    seen.add(id);
    out.push(item);
  });
  return out;
}

function waitingReceivableEvidenceItems(
  card: LiffFinancialWorkCard,
  pendingSlips: ReceivableListItem[],
  selectedPendingSlipKey: string | null | undefined,
  ownerUserId?: string,
  projectedEvidence: EvidenceItem[] = [],
): EvidenceItem[] {
  const title = displayWorkTitle(card.title)
    .replace(/\s*ค้าง\s*[\d,.]+\s*บาท.*$/i, "")
    .replace(/^ยอด/, "")
    .trim();
  const amountMinor = card.amountSummary?.primaryAmountMinor;
  const currency = card.amountSummary?.currency || "THB";
  const receivableNav = card.availableActions.find(
    (action) => action.actionType === "open_data" && action.dataNav,
  )?.dataNav;
  const receivable: EvidenceItem = {
    id: `ev-waiting-receivable-${card.objectRef.id}`,
    label: title ? `ยอด${title}` : "ยอดค้าง",
    status: "needs_review",
    detail:
      amountMinor != null
        ? `ยอดค้าง ${formatAmountMinor(amountMinor, currency)}`
        : undefined,
    sourceLabel: "ข้อมูลลูกหนี้",
    matchedObjectId: card.objectRef.id,
    occurredAt:
      contextRowValue(card, "occurred_at") ||
      contextRowValue(card, "created_at") ||
      undefined,
    expiresAt:
      contextRowValue(card, "due_at") ||
      contextRowValue(card, "expires_at") ||
      card.outstanding?.items?.find(
        (row) => row.objectRef.id === card.objectRef.id,
      )?.expiresAt ||
      card.outstanding?.items?.[0]?.expiresAt ||
      card.backlogChildren?.find(
        (row) => row.objectRef.id === card.objectRef.id,
      )?.expiresAt ||
      undefined,
    dataNav: receivableNav,
  };

  const orderedSlips = [...pendingSlips].sort((left, right) => {
    if (!selectedPendingSlipKey) return 0;
    const leftSelected =
      pendingSlipOptionId(left, pendingSlips.indexOf(left)) ===
      selectedPendingSlipKey;
    const rightSelected =
      pendingSlipOptionId(right, pendingSlips.indexOf(right)) ===
      selectedPendingSlipKey;
    return Number(rightSelected) - Number(leftSelected);
  });
  const pendingEvidence = orderedSlips.map((slip) => {
    const originalIndex = pendingSlips.indexOf(slip);
    const slipAmountMinor = pendingSlipOriginalAmountMinor(slip);
    const remainingMinor = pendingSlipRemainingAmountMinor(slip);
    const showRemaining =
      remainingMinor != null &&
      slipAmountMinor != null &&
      remainingMinor < slipAmountMinor;
    const transferor =
      slip.fields
        .find((field) =>
          ["slip_sender", "slip_sender_name", "sender_name"].includes(field.key),
        )
        ?.value?.trim() ||
      slip.title?.trim() ||
      undefined;
    return {
      id: `ev-waiting-slip-${pendingSlipOptionId(slip, originalIndex)}`,
      label: "สลิปจาก LINE",
      status: "complete" as const,
      detail:
        slipAmountMinor != null
          ? showRemaining && remainingMinor != null
            ? `ยอดโอน ${formatAmountMinor(slipAmountMinor, "THB")} · เหลือ ${formatAmountMinor(remainingMinor, "THB")}`
            : `ยอดโอน ${formatAmountMinor(slipAmountMinor, "THB")}`
          : undefined,
      imageUrl: pickReceivableSlipImageUrl(slip, ownerUserId) || undefined,
      sourceLabel: "LINE OA",
      transferorLabel: transferor,
      transferAmountMinor: slipAmountMinor ?? undefined,
      occurredAt: slip.transferDate || slip.createdAt || undefined,
      matchedObjectId: card.objectRef.id,
    };
  });
  const projectedSlips =
    pendingEvidence.length > 0
      ? []
      : [...(card.evidenceItems ?? []), ...projectedEvidence]
          .filter(
            (item) =>
              !isLedgerEvidenceItem(item) &&
              (Boolean(item.imageUrl) ||
                Boolean(item.transferorLabel) ||
                /สลิป|LINE/i.test(`${item.label} ${item.sourceLabel ?? ""}`)),
          )
          .filter(
            (item, index, all) =>
              all.findIndex((candidate) => candidate.id === item.id) === index,
          )
          .map((item) => ({
            ...item,
            status: "complete" as const,
            matchedObjectId: item.matchedObjectId || card.objectRef.id,
            label: /LINE/i.test(`${item.label} ${item.sourceLabel ?? ""}`)
              ? "สลิปจาก LINE"
              : item.label,
          }));

  return [receivable, ...pendingEvidence, ...projectedSlips];
}

function isCorrectionAction(action: ActionDirective): boolean {
  if (
    action.actionType === "confirm_match" ||
    action.actionType === "park_slip" ||
    action.actionType === "confirm_split" ||
    action.actionType === "reject_split" ||
    action.actionType === "reject_retro"
  ) {
    return false;
  }
  const label = action.label || "";
  return (
    label.includes("จับคู่") ||
    action.actionType.includes("correct") ||
    action.actionType.includes("match") ||
    action.actionType.includes("resplit")
  );
}

type ProvenanceRow = {
  label: string;
  value: string;
  editNav?: {
    workerId: string;
    recordId?: string;
    scrollTo?: string;
    highlight?: string;
  };
};

type ProvenanceSection = {
  title: string;
  rows: ProvenanceRow[];
};

function formatProvenanceDatetime(iso: string | undefined): string {
  const raw = (iso || "").trim();
  if (!raw) return "—";
  const dateLabel = formatThaiDateCard(raw);
  if (!dateLabel || dateLabel === "—") return "—";
  const parsed = parseDatetimeInput(raw);
  if (!parsed) return dateLabel;
  const { timeLabel } = formatThaiDatetimePartsFromDate(parsed);
  return timeLabel ? `${dateLabel} ${timeLabel}` : dateLabel;
}

function contextRowValue(
  card: LiffFinancialWorkCard,
  key: string,
): string | null {
  const row = card.contextRows.find((item) => item.key === key);
  const value = String(row?.value ?? "").trim();
  return value || null;
}

function recordText(value: unknown): string {
  const text = getDisplayText(value).trim();
  return text || "—";
}

function recordAmount(value: unknown): string {
  const raw = getDisplayText(value).trim();
  if (!raw) return "—";
  const n = Number(raw);
  if (Number.isFinite(n)) return formatAmount(n);
  return raw;
}

function cardWorkKind(card: LiffFinancialWorkCard): string {
  return (
    card.contextRows.find((row) => row.key === "kind")?.value?.trim() ||
    card.objectRef.kind
  );
}

function provenanceDefaultOpen(card: LiffFinancialWorkCard, closed: boolean): boolean {
  if (closed) return true;
  const kind = cardWorkKind(card);
  if (kind === "payment" || kind === "expense") return true;
  if (isSlipOwnerAskCard(card)) return true;
  return false;
}

function buildClosedProvenanceSections(
  card: LiffFinancialWorkCard,
  record?: IncomeRecord | null,
  options?: { incomeRecordMissing?: boolean },
): {
  source: ProvenanceRow[];
  payment: ProvenanceSection;
  customer: ProvenanceSection;
} {
  const sourceLabel = closedProvenanceSourceLabel(card, record);
  const dataNav = card.availableActions.find(
    (action) => action.actionType === "open_data",
  )?.dataNav;

  if (record) {
    const occurredRaw =
      record.created_at ||
      record.record_date ||
      record.recorded_at ||
      record.transfer_datetime ||
      "";
    const updatedRaw =
      record.updated_at || record.recorded_at || occurredRaw;
    const party =
      recordText(record.party_name) !== "—"
        ? recordText(record.party_name)
        : displayWorkTitle(card.title);
    const transferor = recordText(record.sender_name);
    const customerName = recordText(record.customer_name);

    const source: ProvenanceRow[] = [
      { label: "แหล่งที่มา", value: sourceLabel || "—" },
      {
        label: "คู่กรณี",
        value: recordText(record.matched_receivable_customer),
      },
      {
        label: "วันที่เกิดรายการ",
        value: formatProvenanceDatetime(String(occurredRaw)),
      },
      {
        label: "อัปเดตล่าสุด",
        value: formatProvenanceDatetime(String(updatedRaw)),
      },
    ];

    const paymentRows: ProvenanceRow[] = [
      { label: "ยอดโอน", value: recordAmount(record.transfer_amount) },
      { label: "จับคู่แล้ว", value: recordAmount(record.allocated_amount) },
      { label: "รอจัดสรร", value: recordAmount(record.unallocated_amount) },
      { label: "ประเภทเงิน", value: recordText(record.money_type) },
      { label: "ส่งบัญชี", value: recordText(record.accounting_export) },
      { label: "สำเนา Google", value: recordText(record.google_mirror) },
    ];

    const customerRows: ProvenanceRow[] = [
      { label: "ผู้โอน/ลูกค้า", value: party },
      {
        label: "ผู้โอน",
        value: transferor !== "—" ? transferor : party,
      },
      {
        label: "ชื่อลูกค้า",
        value: customerName !== "—" ? customerName : party,
        editNav: dataNav?.workerId ? dataNav : undefined,
      },
    ];

    return {
      source,
      payment: { title: "การชำระเงิน", rows: paymentRows },
      customer: { title: "ลูกค้า", rows: customerRows },
    };
  }

  if (options?.incomeRecordMissing) {
    const dataNav = card.availableActions.find(
      (action) => action.actionType === "open_data",
    )?.dataNav;
    const source: ProvenanceRow[] = [
      { label: "แหล่งที่มา", value: sourceLabel || "—" },
      { label: "คู่กรณี", value: "—" },
      { label: "วันที่เกิดรายการ", value: "—" },
      { label: "อัปเดตล่าสุด", value: "—" },
    ];
    const dash = (label: string): ProvenanceRow => ({ label, value: "—" });
    return {
      source,
      payment: {
        title: "การชำระเงิน",
        rows: [
          dash("ยอดโอน"),
          dash("จับคู่แล้ว"),
          dash("รอจัดสรร"),
          dash("ประเภทเงิน"),
          dash("ส่งบัญชี"),
          dash("สำเนา Google"),
        ],
      },
      customer: {
        title: "ลูกค้า",
        rows: [
          dash("ผู้โอน/ลูกค้า"),
          dash("ผู้โอน"),
          {
            label: "ชื่อลูกค้า",
            value: "—",
            editNav: dataNav?.workerId ? dataNav : undefined,
          },
        ],
      },
    };
  }

  const occurredRaw =
    contextRowValue(card, "created_at") ||
    contextRowValue(card, "occurred_at") ||
    card.timelineSummary?.occurredAt ||
    card.timelineEvents?.[0]?.occurredAtLabel ||
    "";
  const updatedRaw =
    contextRowValue(card, "updated_at") ||
    card.timelineSummary?.occurredAt ||
    card.timelineEvents?.[0]?.occurredAtLabel ||
    "";
  const occurredLabel = formatProvenanceDatetime(occurredRaw);
  const updatedLabel = formatProvenanceDatetime(updatedRaw);

  const evidence = card.evidenceItems?.[0];
  const counterpart =
    contextRowValue(card, "counterparty") ||
    evidence?.counterpartLabel?.trim() ||
    card.consequenceRows?.[0]?.label?.trim() ||
    "—";

  const currency = card.amountSummary?.currency || "THB";
  const transferMinor = card.amountSummary?.primaryAmountMinor ?? 0;
  const matchedMinorRaw = contextRowValue(card, "allocated_amount");
  const matchedMinor =
    matchedMinorRaw != null && /^\d+$/.test(matchedMinorRaw)
      ? Number(matchedMinorRaw)
      : evidence?.matchedAmountMinor ??
        card.outstanding?.items?.[0]?.paidAmountMinor ??
        transferMinor;
  const unallocatedRaw = contextRowValue(card, "unallocated_amount");
  const unallocatedMinor =
    card.outstanding?.remainingAmountMinor ??
    (unallocatedRaw != null && /^\d+$/.test(unallocatedRaw)
      ? Number(unallocatedRaw)
      : Math.max(0, transferMinor - matchedMinor));

  const kind = contextRowValue(card, "kind");
  const direction = contextRowValue(card, "direction");
  const moneyType =
    contextRowValue(card, "money_type") ||
    (direction === "out" || kind === "expense" ? "จ่าย" : "รับชำระ");

  const accounting = contextRowValue(card, "accounting_export") || "—";
  const google = contextRowValue(card, "google_mirror") || "—";

  const party = displayWorkTitle(card.title);
  const sourceParty = partyFromSourceLabel(sourceLabel);
  const transferor =
    contextRowValue(card, "transferor") ||
    contextRowValue(card, "customer") ||
    sourceParty ||
    party;

  const source: ProvenanceRow[] = [
    { label: "แหล่งที่มา", value: sourceLabel || "—" },
    { label: "คู่กรณี", value: counterpart || "—" },
    { label: "วันที่เกิดรายการ", value: occurredLabel },
    { label: "อัปเดตล่าสุด", value: updatedLabel },
  ];

  const paymentRows: ProvenanceRow[] = [
    { label: "ยอดโอน", value: formatAmountMinor(transferMinor, currency) },
    { label: "จับคู่แล้ว", value: formatAmountMinor(matchedMinor, currency) },
    {
      label: "รอจัดสรร",
      value: formatAmountMinor(unallocatedMinor, currency),
    },
    { label: "ประเภทเงิน", value: moneyType },
    {
      label: "ส่งบัญชี",
      value: displayContextValue("accounting_export", accounting),
    },
    {
      label: "สำเนา Google",
      value: displayContextValue("google_mirror", google),
    },
  ];

  const customerRows: ProvenanceRow[] = [
    { label: "ผู้โอน/ลูกค้า", value: party || "—" },
    { label: "ผู้โอน", value: transferor || "—" },
    {
      label: "ชื่อลูกค้า",
      value: party || "—",
      editNav: dataNav?.workerId ? dataNav : undefined,
    },
  ];

  return {
    source,
    payment: { title: "การชำระเงิน", rows: paymentRows },
    customer: { title: "ลูกค้า", rows: customerRows },
  };
}

function ProvenanceFieldRow(props: {
  row: ProvenanceRow;
  onOpenDataNav?: (nav: {
    workerId: string;
    recordId?: string;
    scrollTo?: string;
    highlight?: string;
  }) => void;
}) {
  const { row } = props;
  return (
    <div className="flex items-start justify-between gap-3 border-b border-aera-lightgray/80 py-2.5 text-sm last:border-b-0">
      <dt className="shrink-0 text-aera-gray">{row.label}</dt>
      <dd className="flex min-w-0 flex-col items-end gap-1.5 text-right text-aera-charcoal">
        <span className="break-words">{row.value}</span>
        {row.editNav && props.onOpenDataNav ? (
          <AeraButton
            type="button"
            variant="outline"
            size="xs"
            className="shrink-0 cursor-pointer border-aera-amber/40 text-aera-amberdark"
            onClick={() => props.onOpenDataNav?.(row.editNav!)}
          >
            แก้ไข
          </AeraButton>
        ) : null}
      </dd>
    </div>
  );
}

function FinancialWorkProvenanceSkeleton() {
  const rowWidths = [
    ["w-20", "w-28"],
    ["w-14", "w-16"],
    ["w-24", "w-32"],
    ["w-20", "w-32"],
  ] as const;

  return (
    <div
      className="space-y-1 pb-1"
      role="status"
      aria-busy="true"
      aria-label="กำลังโหลดข้อมูลต้นทางและประวัติ"
      data-testid="fw-provenance-panel"
    >
      <div data-testid="fw-provenance-loading">
        <dl>
          {rowWidths.map(([labelWidth, valueWidth], index) => (
            <div
              key={`source:${index}`}
              className="flex items-center justify-between gap-3 border-b border-aera-lightgray/80 py-2.5"
            >
              <DetailPulse className={`h-3.5 ${labelWidth}`} />
              <DetailPulse className={`h-3.5 ${valueWidth}`} />
            </div>
          ))}
        </dl>
        {[6, 3].map((rowCount, sectionIndex) => (
          <div key={sectionIndex} className="pt-3">
            <div className="flex items-center justify-between gap-2 border-b border-aera-lightgray/80 pb-2">
              <DetailPulse className="h-4 w-20" />
              <DetailPulse className="h-3 w-12" />
            </div>
            <dl>
              {Array.from({ length: rowCount }).map((_, rowIndex) => (
                <div
                  key={rowIndex}
                  className="flex items-center justify-between gap-3 border-b border-aera-lightgray/80 py-2.5 last:border-b-0"
                >
                  <DetailPulse
                    className={`h-3.5 ${rowIndex % 2 === 0 ? "w-16" : "w-20"}`}
                  />
                  <DetailPulse
                    className={`h-3.5 ${rowIndex % 3 === 0 ? "w-24" : "w-16"}`}
                  />
                </div>
              ))}
            </dl>
          </div>
        ))}
      </div>
    </div>
  );
}

function FinancialWorkProvenancePanel(props: {
  card: LiffFinancialWorkCard;
  workspaceId?: string | null;
  onOpenDataNav?: (nav: {
    workerId: string;
    recordId?: string;
    scrollTo?: string;
    highlight?: string;
  }) => void;
}) {
  const { data: record, isLoading, isFetching } =
    useFinancialWorkIncomeRecord(props.card, props.workspaceId);
  const loading = isLoading || (isFetching && !record);

  if (loading) {
    return <FinancialWorkProvenanceSkeleton />;
  }

  const sections = buildClosedProvenanceSections(props.card, record);

  return (
    <div className="space-y-1 pb-1" data-testid="fw-provenance-panel">
      <dl>
        {sections.source.map((row) => (
          <ProvenanceFieldRow key={row.label} row={row} />
        ))}
      </dl>
      {[sections.payment, sections.customer].map((section) => (
        <div key={section.title} className="pt-3">
          <div className="flex items-baseline justify-between gap-2 border-b border-aera-lightgray/80 pb-2">
            <h4 className="text-sm font-semibold text-aera-amberdark">
              {section.title}
            </h4>
            <span className="text-[11px] text-aera-gray">
              {section.rows.length} รายการ
            </span>
          </div>
          <dl>
            {section.rows.map((row) => (
              <ProvenanceFieldRow
                key={`${section.title}:${row.label}`}
                row={row}
                onOpenDataNav={props.onOpenDataNav}
              />
            ))}
          </dl>
        </div>
      ))}
    </div>
  );
}

function workDetailBadgeClass(tone: "ok" | "warn" | "danger"): string {
  return [
    "inline-flex rounded-xl px-2 py-0.5 text-[11px] font-bold leading-4",
    tone === "ok"
      ? "bg-emerald-50 text-emerald-800"
      : tone === "danger"
        ? "bg-aera-redlight text-aera-red"
        : "bg-aera-warmcream text-aera-amberdark",
  ].join(" ");
}

function isHiddenWorkContextRow(row: { key: string; label: string }): boolean {
  return (
    row.key === "batch_id" ||
    row.label === "batch_id" ||
    row.key === "batch_size" ||
    row.key === "batch_closed" ||
    row.key === "feed" ||
    row.key === "ask_kind" ||
    row.key === "kind"
  );
}

function looksLikeAmountDetail(value: string | undefined): boolean {
  const text = (value || "").trim();
  return Boolean(text) && /[0-9]/.test(text) && (text.includes("฿") || /บาท|\d/.test(text));
}

function evidenceCoversChild(item: EvidenceItem, row: BacklogChildRow): boolean {
  if (item.matchedObjectId && item.matchedObjectId === row.objectRef.id) return true;
  if (item.dataNav?.recordId && item.dataNav.recordId === row.dataNav?.recordId) {
    return true;
  }
  return item.label.includes(row.label);
}

function matchFactsFromChild(
  item: Pick<
    EvidenceItem,
    | "occurredAt"
    | "expiresAt"
    | "originalAmountMinor"
    | "outstandingAmountMinor"
    | "matchedAmountMinor"
    | "remainingAmountMinor"
  >,
  child?: BacklogChildRow,
): Pick<
  EvidenceItem,
  | "occurredAt"
  | "expiresAt"
  | "originalAmountMinor"
  | "outstandingAmountMinor"
  | "matchedAmountMinor"
  | "remainingAmountMinor"
> {
  return {
    occurredAt: item.occurredAt || child?.occurredAt,
    expiresAt: item.expiresAt || child?.expiresAt,
    originalAmountMinor: item.originalAmountMinor ?? child?.originalAmountMinor,
    outstandingAmountMinor: item.outstandingAmountMinor,
    matchedAmountMinor: item.matchedAmountMinor ?? child?.paidAmountMinor,
    remainingAmountMinor: item.remainingAmountMinor ?? child?.remainingAmountMinor,
  };
}

function evidenceItemFromPaidChild(row: BacklogChildRow): EvidenceItem | null {
  const matchedMinor = row.paidAmountMinor ?? 0;
  const hasAppliedMatch =
    matchedMinor > 0 ||
    row.status === "paid" ||
    Boolean(row.slipImageUrl) ||
    Boolean(row.dataNav?.recordId && row.dataNav.workerId === "bill-lin-income");
  if (!hasAppliedMatch) return null;
  const amountDetail =
    matchedMinor > 0
      ? formatAmountMinor(matchedMinor, row.currency || "THB")
      : looksLikeAmountDetail(row.slipLabel)
        ? row.slipLabel!.trim()
        : undefined;
  const remaining = row.remainingAmountMinor;
  const outstanding =
    remaining != null && matchedMinor > 0
      ? remaining + matchedMinor
      : row.originalAmountMinor ?? undefined;
  return {
    id: `ev-${row.objectRef.id}-slip`,
    label: slipTransferLabel(row.slipPartyName, row.label),
    status: "complete",
    detail: amountDetail,
    imageUrl: row.slipImageUrl,
    dataNav: row.dataNav,
    matchedObjectId: row.objectRef.id,
    counterpartLabel: `ยอด${row.label}`,
    counterpartDetail: amountDetail,
    sourceLabel: row.slipPartyName?.trim()
      ? "LINE OA"
      : row.dataNav?.workerId === "bill-lin-income"
        ? "LINE OA"
        : undefined,
    ...matchFactsFromChild(
      {
        outstandingAmountMinor: outstanding,
        matchedAmountMinor: matchedMinor > 0 ? matchedMinor : undefined,
        remainingAmountMinor: remaining,
      },
      row,
    ),
  };
}

function childClosedAmountMinor(row: BacklogChildRow): number | null {
  if (row.status === "paid" && row.paidAmountMinor != null) return row.paidAmountMinor;
  if (row.remainingAmountMinor != null) return row.remainingAmountMinor;
  return null;
}

/** Match math for person rows — only when server already applied a payment. */
function childMatchFactsItem(row: BacklogChildRow): EvidenceItem | null {
  const matched = row.paidAmountMinor;
  if (matched == null || matched <= 0) return null;
  const remaining = row.remainingAmountMinor;
  const outstanding =
    remaining != null
      ? remaining + matched
      : row.originalAmountMinor ?? null;
  return {
    id: `child-match-${row.objectRef.id}`,
    label: row.label,
    status: "complete",
    occurredAt: row.occurredAt,
    originalAmountMinor: row.originalAmountMinor,
    outstandingAmountMinor: outstanding ?? undefined,
    matchedAmountMinor: matched,
    remainingAmountMinor: remaining,
  };
}

function closedMatchEvidenceItems(card: LiffFinancialWorkCard): EvidenceItem[] {
  const isBatch = isReceivableBatchCard(card);
  const focusId =
    !isBatch && card.objectRef.kind === "receivable"
      ? card.objectRef.id.trim()
      : "";
  const children = card.backlogChildren ?? [];
  const allEvidence = card.evidenceItems ?? [];
  // Member cards inherit the batch projection — drop other people's slips.
  const existing = allEvidence.filter((item) => {
    if (!focusId) return true;
    return (
      item.matchedObjectId === focusId ||
      item.id.includes(focusId)
    );
  });

  // Batch parent: every applied match on the roster. Member / single card: only
  // this receivable — including จ่ายบางส่วน so หลักฐาน shows the prior slip.
  const rows = focusId
    ? children.filter((row) => row.objectRef.id === focusId)
    : children;

  for (const row of rows) {
    const item = evidenceItemFromPaidChild(row);
    if (!item) continue;
    if (existing.some((ev) => evidenceCoversChild(ev, row))) continue;
    existing.push(item);
  }

  if (
    focusId &&
    !existing.some(
      (ev) =>
        ev.matchedObjectId === focusId ||
        ev.id.includes(focusId),
    )
  ) {
    const outstanding =
      card.outstanding?.items?.find((row) => row.objectRef.id === focusId) ??
      null;
    if (outstanding) {
      const item = evidenceItemFromPaidChild(outstanding);
      if (
        item &&
        !existing.some((ev) => evidenceCoversChild(ev, outstanding))
      ) {
        existing.push(item);
      }
    }
  }

  const cardMatchedAt =
    card.requirementStatus.complete || card.surfaceState === "closed"
      ? contextRowValue(card, "matched_at") ||
        contextRowValue(card, "updated_at") ||
        undefined
      : undefined;
  return existing.map((item) => {
    const enriched = enrichClosedMatchItem(item, children);
    const recordId = enriched.dataNav?.recordId?.trim();
    const transferor = enriched.transferorLabel?.trim() || "";
    const childTransferMinor = children.reduce((largest, child) => {
      const text = `${child.label} ${child.slipLabel || ""}`;
      if (
        !/สลิป|LINE/i.test(text) ||
        (transferor && !text.includes(transferor))
      ) {
        return largest;
      }
      return Math.max(largest, childClosedAmountMinor(child) ?? 0);
    }, 0);
    const allocationTotalMinor = recordId
      ? allEvidence
          .filter(
            (candidate) =>
              candidate.dataNav?.recordId?.trim() === recordId,
          )
          .reduce(
            (total, candidate) =>
              total + (candidate.matchedAmountMinor ?? 0),
            0,
          )
      : 0;
    const sharedTransferMinor =
      enriched.transferAmountMinor ??
      Math.max(childTransferMinor, allocationTotalMinor);
    return {
      ...enriched,
      ...(cardMatchedAt && !enriched.matchedAt
        ? { matchedAt: cardMatchedAt }
        : {}),
      ...(sharedTransferMinor > 0
        ? { transferAmountMinor: sharedTransferMinor }
        : {}),
    };
  });
}

function partyFromSourceLabel(value: string): string {
  const parts = value.split("·").map((part) => part.trim()).filter(Boolean);
  return parts.length > 1 ? parts[parts.length - 1]! : "";
}

/** Announcement ยอดค้าง TTL is 14 days — used when feed omits expiresAt. */
const ANNOUNCEMENT_DUE_MS = 14 * 24 * 60 * 60 * 1000;

function deriveAnnouncementDueAt(occurredAt?: string | null): string | undefined {
  const parsed = parseDatetimeInput((occurredAt || "").trim());
  if (!parsed) return undefined;
  return new Date(parsed.getTime() + ANNOUNCEMENT_DUE_MS).toISOString();
}

function isLedgerEvidenceItem(item: EvidenceItem): boolean {
  return (
    item.sourceLabel === "ข้อมูลลูกหนี้" ||
    item.id.includes("-out-") ||
    item.id.endsWith("-counterpart")
  );
}

function evidenceThumbForItem(item: EvidenceItem): {
  glyph: DocThumbGlyph;
  incoming: boolean;
  review: boolean;
} {
  const hay = `${item.label} ${item.sourceLabel ?? ""} ${item.detail ?? ""}`;
  const review = item.status === "needs_review";
  if (isLedgerEvidenceItem(item)) {
    if (/ยอดค้างจาก|ยอดค้าง|ชุดยอดค้าง|ข้อมูลลูกหนี้/.test(hay) || item.id.includes("-out-")) {
      return { glyph: "receipt", incoming: true, review };
    }
    return { glyph: "bank" as const, incoming: true, review };
  }
  if (/ใบแจ้งหนี้จาก|invoice|ยังจ่ายไม่ได้|ยังไม่จ่าย|ใบแจ้งหนี้|ใบกำกับ/i.test(hay)) {
    return { glyph: "invoice", incoming: false, review };
  }
  if (/ใบเสร็จจาก|ใบเสร็จ|รายจ่าย|เงินออก|ตลาดสด|เงินสดหน้าร้าน/.test(hay)) {
    return { glyph: "withdraw", incoming: false, review };
  }
  if (/รายการธนาคาร|ยังไม่รู้ผู้โอน|ไม่ทราบผู้โอน|ไม่รู้ผู้โอน/.test(hay)) {
    return { glyph: "bank", incoming: true, review };
  }
  if (/ยอดค้างจาก|ยอดค้าง|ชุดยอดค้าง/.test(hay)) {
    return { glyph: "receipt", incoming: true, review };
  }
  // สลิปจาก LINE/ระบบ (and other money-in slips)
  return { glyph: "deposit", incoming: true, review };
}

/** Slip + outstanding rows for an unmatched ask so the shell matches the compare mock. */
function comparisonEvidenceItems(
  card: LiffFinancialWorkCard,
  workspaceId = "",
  ownerUserId?: string,
): EvidenceItem[] {
  const matched = closedMatchEvidenceItems(card);
  if (matched.length > 0) return matched;
  if (!isSlipOwnerAskCard(card)) return [];

  const amount =
    card.amountSummary != null
      ? formatAmountMinor(
          card.amountSummary.primaryAmountMinor,
          card.amountSummary.currency,
        )
      : "";
  const sourceRow = card.contextRows.find(
    (row) => row.key === "source" || row.label.includes("แหล่ง"),
  );
  const sourceValue = displayContextValue(
    sourceRow?.key ?? "source",
    sourceRow?.value ?? card.subtitle ?? "",
  );
  const askOptionParty = (card.consequenceRows ?? [])
    .map((row) =>
      row.label
        .replace(/\s*\([\d,.]+\s*บาท\)\s*$/, "")
        .replace(/^ยอด/, "")
        .trim(),
    )
    .find((name) => Boolean(name));
  const party = (
    contextRowValue(card, "transferor") ||
    contextRowValue(card, "customer") ||
    partyFromSourceLabel(sourceValue) ||
    card.title.match(/(?:ของ|จาก)\s*(.+)$/)?.[1]?.trim() ||
    card.title.split("·")[0]?.trim() ||
    askOptionParty ||
    ""
  )
    .replace(/\s*ยังจับคู่ไม่ได้.*$/, "")
    .replace(/\s*[\d,.]+\s*บาท.*$/, "")
    .replace(/\s*แบ่งยอด.*$/, "")
    .trim();
  const slipNav = card.availableActions.find((action) => action.dataNav)?.dataNav;
  const slipImage = resolveQueueCardImageUrl(card, workspaceId, ownerUserId);
  const transferMinor = card.amountSummary?.primaryAmountMinor;
  const slip: EvidenceItem = {
    id: `ev-compare-slip-${card.objectRef.id}`,
    label: sourceValue.split("·")[0]?.trim() || "ไม่ระบุแหล่งที่มา",
    status: "complete",
    detail: amount ? `ยอดโอน ${amount}` : undefined,
    sourceLabel: sourceValue || "ไม่ระบุแหล่งที่มา",
    transferorLabel: party || undefined,
    transferAmountMinor:
      transferMinor != null && transferMinor > 0 ? transferMinor : undefined,
    occurredAt:
      contextRowValue(card, "occurred_at") ||
      contextRowValue(card, "created_at") ||
      card.timelineSummary?.occurredAt ||
      card.timelineEvents?.[0]?.occurredAtLabel,
    imageUrl: slipImage || undefined,
    dataNav: slipNav,
  };
    const outstanding = (card.consequenceRows ?? []).map((row) => {
    const askKind = askKindFromCard(card);
    const rowLabel = row.label
      .replace(/\s*\([\d,.]+\s*บาท\)\s*$/, "")
      .trim();
    const effectiveMinor =
      row.amountMinor != null && row.amountMinor > 0
        ? row.amountMinor
        : askKind === "slip_confirm" && card.amountSummary
          ? card.amountSummary.primaryAmountMinor
          : row.amountMinor;
    const rowAmount =
      effectiveMinor != null
        ? formatAmountMinor(effectiveMinor, row.currency || "THB")
        : "";
    const isConfirm =
      askKind === "slip_confirm" && (card.consequenceRows?.length ?? 0) === 1;
    const recvNav =
      row.objectRef.kind === "receivable" && row.objectRef.id
        ? {
            workerId: "bill-lin-receivable",
            recordId: row.objectRef.id.replace(/^redis:/, ""),
            highlight: row.objectRef.id.replace(/^redis:/, ""),
          }
        : undefined;
    const mate =
      card.outstanding?.items?.find(
        (item) => item.objectRef.id === row.objectRef.id,
      ) ||
      card.backlogChildren?.find(
        (item) => item.objectRef.id === row.objectRef.id,
      ) ||
      card.outstanding?.items?.find(
        (item) =>
          effectiveMinor != null &&
          item.remainingAmountMinor === effectiveMinor,
      ) ||
      card.backlogChildren?.find(
        (item) =>
          effectiveMinor != null &&
          item.remainingAmountMinor === effectiveMinor,
      );
    const ownOccurredAt =
      row.occurredAt?.trim() || mate?.occurredAt?.trim() || undefined;
    // Do not fall back to slip time for display — that stamped every ยอด with the same day.
    const occurredAt = ownOccurredAt;
    const expiresAt =
      row.expiresAt?.trim() ||
      mate?.expiresAt?.trim() ||
      deriveAnnouncementDueAt(ownOccurredAt || slip.occurredAt) ||
      undefined;
    return {
      id: `ev-compare-out-${row.objectRef.id}`,
      label: rowLabel
        ? `${rowLabel.startsWith("ยอด") ? "" : "ยอด"}${rowLabel}`
        : isConfirm
          ? "ยอดที่จะปิด"
          : "ยอดค้าง",
      status: "needs_review" as const,
      // Match slip_confirm evidence: ยอด + ครบกำหนด (AeraTransferMeta), not bare ยอดค้าง.
      detail: rowAmount ? `ยอด ${rowAmount}` : row.label,
      sourceLabel: "ข้อมูลลูกหนี้",
      matchedObjectId: row.objectRef.id,
      occurredAt,
      expiresAt,
      dataNav: recvNav,
      outstandingAmountMinor:
        effectiveMinor != null && effectiveMinor > 0 ? effectiveMinor : undefined,
      matchedAmountMinor:
        effectiveMinor != null && effectiveMinor > 0 ? effectiveMinor : undefined,
    };
  });
  return outstanding.length > 0 ? [slip, ...outstanding] : [slip];
}

function enrichClosedMatchItem(
  item: EvidenceItem,
  children?: BacklogChildRow[],
): EvidenceItem {
  const child = (children ?? []).find((row) => evidenceCoversChild(item, row));
  const slipName = (child?.slipPartyName || "").trim();
  const transferor =
    item.transferorLabel?.trim() ||
    slipName ||
    item.label.match(/^สลิป(?:โอน)?ของ\s+(.+)$/)?.[1]?.trim() ||
    "";
  const source = item.sourceLabel?.trim() || "";
  const patched = transferor
    ? {
        ...item,
        label: /LINE/i.test(source) ? "สลิปจาก LINE" : source || "สลิป",
        transferorLabel: transferor,
      }
    : item;
  const facts = matchFactsFromChild(patched, child);
  const transferAmount =
    patched.detail ||
    patched.counterpartDetail ||
    (facts.matchedAmountMinor != null
      ? formatAmountMinor(facts.matchedAmountMinor)
      : undefined);
  if (patched.counterpartLabel && patched.counterpartDetail) {
    return { ...patched, ...facts, detail: transferAmount };
  }
  if (!child) return { ...patched, ...facts, detail: transferAmount };
  const amount =
    patched.detail ||
    (child.paidAmountMinor != null
      ? formatAmountMinor(child.paidAmountMinor, child.currency || "THB")
      : undefined);
  return {
    ...patched,
    ...facts,
    detail: transferAmount,
    counterpartLabel: patched.counterpartLabel || `ยอด${child.label}`,
    counterpartDetail: patched.counterpartDetail || amount,
  };
}

function FinancialWorkDetailMissing(props: {
  embeddedInModal?: boolean;
}) {
  return (
    <div
      role="status"
      data-testid="fw-detail-missing"
      className={
        props.embeddedInModal
          ? "flex min-h-[calc(92dvh-10rem)] flex-col items-center justify-center px-2 py-8 text-center"
          : "flex min-h-[16rem] flex-col items-center justify-center px-2 py-10 text-center"
      }
    >
      <span
        className="flex h-14 w-14 items-center justify-center rounded-full bg-aera-offwhite text-aera-gray"
        aria-hidden
      >
        <Search className="h-6 w-6" strokeWidth={1.75} />
      </span>
      <p className="mt-4 text-base font-semibold text-aera-charcoal">
        ไม่พบรายละเอียดงานนี้
      </p>
      <p className="mt-2 max-w-[18rem] text-sm leading-relaxed text-aera-gray">
        งานนี้อาจปิดไปแล้ว หรือเปิดจากข้อความเก่าที่ไม่ตรงกับข้อมูลปัจจุบัน
      </p>
    </div>
  );
}

export function FinancialWorkDetailView(props: {
  card: LiffFinancialWorkCard | null;
  missing?: boolean;
  loading?: boolean;
  recovery: OperationRecoveryState;
  onAction: (action: ActionDirective) => void;
  onRetryRecovery?: () => void;
  lineEntryRevision?: number | null;
  onAckLineStale?: () => void;
  onReloadProjection?: () => void;
  focusPanel?: "outstanding" | "children" | null;
  onOpenChild?: (objectRef: { kind: string; id: string }) => void;
  /** Leave chase sheet and keep detail open. */
  onBackFromChase?: () => void;
  /** When true, omit page back-link and fixed sticky (modal provides chrome). */
  embeddedInModal?: boolean;
  onOpenDataNav?: (nav: {
    workerId: string;
    recordId?: string;
    scrollTo?: string;
    highlight?: string;
  }) => void;
  workspaceId?: string | null;
  selectedAskId?: string | null;
  onSelectAskOption?: (id: string | null) => void;
  pendingSlipItems?: ReceivableListItem[];
  selectedPendingSlipKey?: string | null;
  onSelectPendingSlip?: (key: string | null) => void;
  pendingSlipsLoading?: boolean;
  ownerUserId?: string | null;
}) {
  const { canMutate, blockedReason } = useFinancialWorkMutationGate();
  const { card, recovery, embeddedInModal = false } = props;
  const searchParams = useSearchParams();
  const imageWorkspaceId = (
    props.workspaceId ||
    searchParams?.get("workspaceId") ||
    searchParams?.get("workspace_id") ||
    ""
  ).trim();
  const imageOwnerUserId = (
    searchParams?.get("owner_user_id") ||
    searchParams?.get("ownerUserId") ||
    getUserIdFromAuthToken() ||
    ""
  ).trim();
  const closedEarly = Boolean(card?.requirementStatus?.complete);
  const provenanceCard = card;
  const { data: incomeRecord } = useFinancialWorkIncomeRecord(
    provenanceCard,
    props.workspaceId,
  );
  const [bookBalanceMajor, setBookBalanceMajor] = useState<number | null>(null);
  const [bookBalanceLoading, setBookBalanceLoading] = useState(false);

  useEffect(() => {
    const ws = (props.workspaceId || imageWorkspaceId || "").trim();
    if (!ws) {
      setBookBalanceMajor(null);
      setBookBalanceLoading(false);
      return;
    }
    let cancelled = false;
    setBookBalanceLoading(true);
    void fetchCombinedWorkspaceTransactionSummary(ws)
      .then((book) => {
        if (cancelled) return;
        setBookBalanceMajor(
          book.income.totalAmount - book.expense.totalAmount,
        );
        setBookBalanceLoading(false);
      })
      .catch(() => {
        if (cancelled) return;
        setBookBalanceMajor(null);
        setBookBalanceLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [props.workspaceId, imageWorkspaceId, props.lineEntryRevision]);

  if (props.loading) {
    return <FinancialWorkDetailSkeleton embeddedInModal={embeddedInModal} />;
  }

  if (props.missing || !card) {
    return (
      <FinancialWorkDetailMissing embeddedInModal={embeddedInModal} />
    );
  }

  const amount = card.amountSummary
    ? formatAmountMinor(
        card.amountSummary.primaryAmountMinor,
        card.amountSummary.currency,
      )
    : null;
  const closed = Boolean(card.requirementStatus?.complete);
  const isAsk = isSlipOwnerAskCard(card);
  const askKind = askKindFromCard(card);
  const waitingReceivable = isWaitingReceivableCard(card);
  const pendingSlips = uniquePendingSlipItems(
    filterReceivableMoneyCandidates(props.pendingSlipItems ?? []),
  );
  const showWaitingSlipPick = waitingReceivable && !isAsk;
  const actionBusy =
    recovery.kind === "submitting" || recovery.kind === "processing";
  const correctionAction = card.availableActions.find(isCorrectionAction);
  const matchActions = card.availableActions.filter(
    (action) => action.actionType === "confirm_match",
  );
  const primary = card.availableActions.find((action) => {
    if (isNavOnlyAction(action)) return false;
    if (closed && isCorrectionAction(action)) return false;
    if (isAsk && action.actionType === "confirm_match") return false;
    return true;
  });
  const otherActions = card.availableActions.filter((action) => {
    if (action === primary || action === correctionAction) return false;
    if (isAsk && action.actionType === "confirm_match") return false;
    if (
      isAsk &&
      (action.actionType === "park_slip" ||
        action.actionType === "confirm_split" ||
        action.actionType === "reject_split" ||
        action.actionType === "reject_retro")
    ) {
      return false;
    }
    if (isAsk && action.actionType === "open_data") {
      return true;
    }
    return !isNavOnlyAction(action);
  });
  const askOptions = card.consequenceRows.map((row) => ({
    row,
    action: matchActions.find(
      (action) =>
        action.objectRef.kind === row.objectRef.kind &&
        action.objectRef.id === row.objectRef.id,
    ),
  }));
  const lineStale =
    props.lineEntryRevision != null &&
    props.lineEntryRevision < card.projectionRevision;

  const statusLabel = workStatusLabel(card);
  const titleText = displayWorkTitle(card.title);
  const isBatch = isReceivableBatchCard(card);
  const fromBatch = isFromBatchCard(card);
  const batchChildren = card.backlogChildren ?? [];
  const unpaidBatchChildren = batchChildren.filter((row) => !isBacklogChildPaid(row));
  const paidBatchChildren = batchChildren.filter((row) => isBacklogChildPaid(row));
  const incomeRecordTarget = financialWorkIncomeRecordTarget(card);
  const incomeTransferMinor = incomeRecord
    ? readIncomeTransferMinor(incomeRecord)
    : 0;
  const projectedEvidence = comparisonEvidenceItems(
    card,
    imageWorkspaceId,
    imageOwnerUserId,
  );
  const matchEvidence = (
    showWaitingSlipPick
      ? waitingReceivableEvidenceItems(
          card,
          pendingSlips,
          props.selectedPendingSlipKey,
          props.ownerUserId?.trim() || imageOwnerUserId,
          projectedEvidence,
        )
      : projectedEvidence
  ).map((item) =>
    item.transferorLabel &&
    item.transferAmountMinor == null &&
    item.dataNav?.recordId === incomeRecordTarget?.recordId &&
    incomeTransferMinor > 0
      ? { ...item, transferAmountMinor: incomeTransferMinor }
      : item,
  );
  const nextLabel = card.nextStep.label?.trim() || "";
  const compareInsight =
    card.requirementStatus.summary &&
    card.requirementStatus.summary !== card.subtitle &&
    card.requirementStatus.summary !== nextLabel &&
    card.requirementStatus.summary !== titleText
      ? card.requirementStatus.summary
      : undefined;
  const showCompareBadge =
    !closed &&
    (isAsk ||
      card.evidenceSummary.needsReview > 0 ||
      card.surfaceState === "needs_review" ||
      card.surfaceState === "risk");
  const nextRedundant =
    !nextLabel ||
    nextLabel === "ไม่ต้องทำอะไรเพิ่ม" ||
    nextLabel.includes(statusLabel) ||
    (statusLabel === "รอสลิป" && nextLabel.includes("รอสลิป"));
  const adviceHeadline = showWaitingSlipPick
    ? pendingSlips.length > 0
      ? "เลือกสลิปที่ตรงกับยอดนี้"
      : nextLabel || "รอสลิปเข้า"
    : nextLabel;
  const showAdvice =
    !closed && (showWaitingSlipPick || !nextRedundant);
  const sourceNote = (card.timelineEvents ?? [])
    .map((e) => e.label)
    .find((label) => /ประกาศ/.test(label));
  const badge = workDetailBadge(card);
  const recDetail = card.consequenceRows
    .map((row) => {
      const amt =
        row.amountMinor != null
          ? formatAmountMinor(row.amountMinor, row.currency || "THB")
          : "";
      return [row.label, amt].filter(Boolean).join(" ");
    })
    .filter(Boolean)
    .join(" + ");
  const planDetail =
    card.contextRows.find((row) => row.key === "recommendation_detail")
      ?.value?.trim() || "";
  const isFifoSplit =
    askKind === "split_suggest" &&
    (card.contextRows.find((row) => row.key === "split_mode")?.value ===
      "fifo" ||
      /ปิดยอดเก่าก่อน/.test(adviceHeadline));
  const isSplitSuggest = askKind === "split_suggest";
  const planLooksLikeNameDots =
    / · /.test(planDetail) && !/ \+ /.test(planDetail);
  const splitPlanLine = isSplitSuggest
    ? buildSplitPlanLineFromRows(card.consequenceRows) ||
      (!planLooksLikeNameDots ? planDetail : "") ||
      (isFifoSplit ? recDetail : "")
    : "";
  const slipAmountMajor =
    card.amountSummary?.primaryAmountMinor != null
      ? card.amountSummary.primaryAmountMinor / 100
      : null;
  const bookAfterMajor =
    bookBalanceMajor != null && slipAmountMajor != null
      ? contextRowValue(card, "direction") === "out" ||
        contextRowValue(card, "kind") === "expense"
        ? bookBalanceMajor - slipAmountMajor
        : bookBalanceMajor + slipAmountMajor
      : null;
  const showBookImpact =
    !showWaitingSlipPick &&
    isAsk &&
    bookBalanceMajor != null &&
    bookAfterMajor != null;
  const showBookImpactSkeleton =
    !showWaitingSlipPick && isAsk && bookBalanceLoading && !showBookImpact;
  const recImpact = card.consequenceRows
    .map((row) => row.status?.trim())
    .find((status) => Boolean(status));
  const provenanceSourcePreview = closedProvenanceSourceLabel(
    card,
    incomeRecord,
    matchEvidence,
  );

  return (
    <div
      data-testid="fw-detail-body"
      data-fw-mode="overview"
      className={
        embeddedInModal
          ? "space-y-3"
          : "space-y-3 pb-[calc(5.5rem+env(safe-area-inset-bottom,0px))]"
      }
    >
      <header className="px-0">
        <span
          data-testid={
            isBatch
              ? "fw-detail-kind-batch"
              : fromBatch
                ? "fw-detail-kind-from-batch"
                : "fw-detail-kind-single"
          }
          className={[
            workDetailBadgeClass(badge.tone),
            embeddedInModal ? "mr-10" : "",
          ].join(" ")}
        >
          {badge.title}
          {lineStale && props.lineEntryRevision != null ? (
            <span data-testid="fw-line-entry-rev"> · ข้อความ LINE เก่า</span>
          ) : null}
        </span>
        {isBatch ? (
          <span className="sr-only">ชุดยอดค้าง</span>
        ) : fromBatch ? (
          <span className="sr-only">จากชุด</span>
        ) : (
          <span className="sr-only">ยอดเดี่ยว</span>
        )}
        <h2 className="mt-2 break-words text-xl font-bold tracking-tight text-aera-charcoal">
          {titleText}
        </h2>
        {card.subtitle ? (
          <p className="mt-1 text-sm text-aera-gray">{card.subtitle}</p>
        ) : null}
        {sourceNote ? (
          <p className="mt-2 text-[11px] text-aera-gray">บันทึกจากประกาศยอดค้าง</p>
        ) : null}
        {fromBatch && (card.backlogChildren?.length ?? 0) > 1 ? (
          <p
            className="mt-2 text-[11px] leading-relaxed text-aera-gray"
            data-testid="fw-from-batch-members"
          >
            ชุดนี้ · {card.backlogChildren!.map((row) => row.label).join(" · ")}
          </p>
        ) : null}

        <div className="mt-4 grid grid-cols-3 gap-2">
          <div className="rounded-xl border border-aera-lightgray bg-aera-white px-3 py-2.5">
            <p className="text-[11px] text-aera-gray">
              {detailAmountCaption(card, closed)}
            </p>
            <p className="mt-1 text-sm font-semibold tabular-nums text-aera-charcoal">
              {amount ?? "—"}
            </p>
          </div>
          <div className="rounded-xl border border-aera-lightgray bg-aera-white px-3 py-2.5">
            <p className="text-[11px] text-aera-gray">สถานะยอด</p>
            <p className="mt-1 text-sm font-semibold text-aera-charcoal">
              {detailBalanceStatus(card, closed)}
            </p>
          </div>
          <div className="rounded-xl border border-aera-lightgray bg-aera-white px-3 py-2.5">
            <p className="text-[11px] text-aera-gray">แหล่งที่มา</p>
            <p
              className="mt-1 min-w-0 break-words text-sm font-semibold text-aera-charcoal"
              data-testid="fw-detail-source"
            >
              {detailSourceLabel(card, matchEvidence, incomeRecord)}
            </p>
          </div>
        </div>
      </header>

      <FinancialWorkRecoveryPanel
        recovery={recovery}
        onRetry={embeddedInModal ? undefined : props.onRetryRecovery}
      />

      {lineStale && props.onAckLineStale ? (
        <FinancialWorkLineStaleBanner
          entryRevision={props.lineEntryRevision!}
          cardRevision={card.projectionRevision}
          onShowCurrent={props.onAckLineStale}
          onReload={props.onReloadProjection}
        />
      ) : null}
      {card.freshness === "stale" ? (
        <div
          role="status"
          data-testid="fw-detail-stale-banner"
          className="rounded-xl border border-amber-200 bg-amber-50 px-3 py-2.5 text-xs text-amber-950"
        >
          <p className="font-medium">
            ข้อมูลรายการนี้เปลี่ยนแปลงแล้ว ต้องโหลดข้อมูลล่าสุดก่อนบันทึกการตัดสินใจหรือแก้ไข
          </p>
          {props.onReloadProjection ? (
            <AeraButton
              type="button"
              variant="secondary"
              size="xs"
              className="mt-2 cursor-pointer"
              onClick={props.onReloadProjection}
            >
              โหลดใหม่
            </AeraButton>
          ) : null}
        </div>
      ) : (
        <FreshnessBanner freshness={card.freshness} />
      )}
      <FinancialWorkOfflineBanner />

      {closed ? (
        <section className="rounded-2xl border border-aera-lightgray bg-aera-offwhite px-4 py-3.5">
          <p className="text-[11px] font-semibold text-aera-gray">การจับคู่</p>
          <div className="mt-1 flex items-start justify-between gap-3">
            <div className="min-w-0">
              <p className="text-sm font-semibold text-aera-charcoal">จับคู่แล้ว</p>
              <p className="mt-0.5 text-xs text-aera-gray">
                {card.subtitle || "รายการจับคู่เดิมตามข้อมูลนำเข้า"}
              </p>
            </div>
            {correctionAction ? (
              <AeraButton
                type="button"
                variant="outline"
                size="xs"
                className="shrink-0 cursor-pointer"
                disabled={!canMutate}
                title={!canMutate ? blockedReason : undefined}
                onClick={() => props.onAction(correctionAction)}
              >
                แก้การจับคู่
              </AeraButton>
            ) : null}
          </div>
        </section>
      ) : null}

      {!closed && showAdvice ? (
        <section
          className="rounded-2xl border border-aera-lightgray bg-aera-white px-4 py-3.5"
          data-testid="fw-advice-card"
        >
          <div className="flex items-center gap-2">
            <span className="flex h-8 w-8 shrink-0 overflow-hidden rounded-full bg-aera-white ring-1 ring-emerald-500/40">
              <AeraImage
                native
                src="/service_img/cmjcrj5ad000gg8xz8icco4jr.png"
                alt="Bill-Lin"
                className="h-full w-full object-cover"
              />
            </span>
            <h3 className="min-w-0 flex-1 text-xs font-semibold text-aera-green">
              คำแนะนำจาก Bill-Lin
            </h3>
            <span className="shrink-0 text-[11px] font-semibold text-aera-green">
              แนะนำ
            </span>
          </div>
          <p className="mt-2 text-sm font-semibold text-aera-charcoal">
            {adviceHeadline}
          </p>
          {showWaitingSlipPick ? (
            props.pendingSlipsLoading ? (
              <p className="mt-2 text-xs text-aera-gray">กำลังโหลดสลิปค้าง…</p>
            ) : pendingSlips.length > 0 ? (
              <ol
                data-testid="fw-pending-slip-options"
                role="radiogroup"
                aria-label="เลือกสลิปที่ตรงกับยอดนี้"
                className="mt-2 space-y-1.5"
              >
                {pendingSlips.map((slip, index) => {
                  const optionId = pendingSlipOptionId(slip, index);
                  const selected = props.selectedPendingSlipKey === optionId;
                  const amountMinor = pendingSlipOriginalAmountMinor(slip);
                  const remainingMinor = pendingSlipRemainingAmountMinor(slip);
                  const showRemaining =
                    remainingMinor != null &&
                    amountMinor != null &&
                    remainingMinor < amountMinor;
                  return (
                    <li key={optionId}>
                      <button
                        type="button"
                        role="radio"
                        aria-checked={selected}
                        className={[
                          "flex w-full min-h-10 items-center gap-2.5 rounded-xl border px-3 py-2 text-left transition-colors",
                          selected
                            ? "border-emerald-500 bg-white ring-1 ring-emerald-500"
                            : "border-aera-lightgray bg-white",
                          !canMutate || actionBusy
                            ? "cursor-not-allowed opacity-60"
                            : "cursor-pointer",
                        ].join(" ")}
                        disabled={!canMutate || actionBusy}
                        title={!canMutate ? blockedReason : undefined}
                        data-testid="fw-pending-slip-option"
                        data-selected={selected ? "true" : "false"}
                        onClick={() =>
                          props.onSelectPendingSlip?.(
                            selected ? null : optionId,
                          )
                        }
                      >
                        <span
                          className={[
                            "flex h-5 w-5 shrink-0 items-center justify-center rounded-full border-2",
                            selected
                              ? "border-emerald-600"
                              : "border-aera-lightgray",
                          ].join(" ")}
                          aria-hidden
                        >
                          {selected ? (
                            <span className="h-2.5 w-2.5 rounded-full bg-emerald-600" />
                          ) : null}
                        </span>
                        <span className="min-w-0 flex-1 truncate font-medium text-aera-charcoal">
                          {index + 1}. {pendingSlipOptionLabel(slip)}
                        </span>
                        {amountMinor != null ? (
                          <span className="shrink-0 text-right tabular-nums">
                            <span className="block text-aera-gray">
                              {formatAmountMinor(amountMinor, "THB")}
                            </span>
                            {showRemaining && remainingMinor != null ? (
                              <span
                                className="block text-[11px] font-medium text-emerald-800"
                                data-testid="fw-pending-slip-remaining"
                              >
                                เหลือ {formatAmountMinor(remainingMinor, "THB")}
                              </span>
                            ) : null}
                          </span>
                        ) : null}
                      </button>
                    </li>
                  );
                })}
              </ol>
            ) : (
              <p className="mt-1 text-xs text-aera-gray">
                ยังไม่มีสลิปค้างให้เลือก
              </p>
            )
          ) : isAsk ? (
            splitPlanLine ? (
              <SplitPlanDetailLine line={splitPlanLine} />
            ) : askOptions.length > 0 ? (
              <ol
                data-testid="fw-ask-options"
                role="radiogroup"
                aria-label={card.nextStep.label || "เลือกยอดที่สลิปนี้เป็นของ"}
                className="mt-2 space-y-1.5"
              >
                {askOptions.map(({ row, action }, index) => {
                  const optionId = askOptionKey(row.objectRef);
                  const selected = props.selectedAskId === optionId;
                  return (
                    <li key={optionId}>
                      {action ? (
                        <button
                          type="button"
                          role="radio"
                          aria-checked={selected}
                          className={[
                            "flex w-full min-h-10 items-center gap-2.5 rounded-xl border px-3 py-2 text-left transition-colors",
                            selected
                              ? "border-emerald-500 bg-white ring-1 ring-emerald-500"
                              : "border-aera-lightgray bg-white",
                            !canMutate || actionBusy
                              ? "cursor-not-allowed opacity-60"
                              : "cursor-pointer",
                          ].join(" ")}
                          disabled={!canMutate || actionBusy}
                          title={!canMutate ? blockedReason : undefined}
                          data-testid="fw-ask-option"
                          data-selected={selected ? "true" : "false"}
                          onClick={() =>
                            props.onSelectAskOption?.(
                              selected ? null : optionId,
                            )
                          }
                        >
                          <span
                            className={[
                              "flex h-5 w-5 shrink-0 items-center justify-center rounded-full border-2",
                              selected
                                ? "border-emerald-600"
                                : "border-aera-lightgray",
                            ].join(" ")}
                            aria-hidden
                          >
                            {selected ? (
                              <span className="h-2.5 w-2.5 rounded-full bg-emerald-600" />
                            ) : null}
                          </span>
                          <span className="min-w-0 flex-1 truncate font-medium text-aera-charcoal">
                            {index + 1}. {row.label}
                          </span>
                          {row.amountMinor != null ? (
                            <span className="shrink-0 tabular-nums text-aera-gray">
                              {formatAmountMinor(row.amountMinor, row.currency || "THB")}
                            </span>
                          ) : null}
                        </button>
                      ) : (
                        <div className="flex items-baseline justify-between gap-3 px-1 text-xs text-aera-charcoal">
                          <span className="min-w-0 truncate">
                            {index + 1}. {row.label}
                          </span>
                          {row.amountMinor != null ? (
                            <span className="shrink-0 tabular-nums text-aera-gray">
                              {formatAmountMinor(row.amountMinor, row.currency || "THB")}
                            </span>
                          ) : null}
                        </div>
                      )}
                    </li>
                  );
                })}
              </ol>
            ) : (
              <p
                className="mt-1 text-xs text-aera-gray"
                data-testid="fw-ask-options-empty"
              >
                ยังไม่มียอดค้างให้จับคู่
              </p>
            )
          ) : recDetail ? (
            <p className="mt-1 text-xs text-aera-gray">{recDetail}</p>
          ) : null}
          {showBookImpactSkeleton ? (
            <div
              className="mt-3 flex items-center justify-between gap-3 border-t border-aera-lightgray pt-2.5"
              data-testid="fw-advice-book-impact-skeleton"
              aria-busy="true"
              aria-label="กำลังโหลดเงินตามสมุด"
            >
              <DetailPulse className="h-3 w-16" />
              <DetailPulse className="h-3.5 w-32 shrink-0" />
            </div>
          ) : showBookImpact ? (
            <div
              className="mt-3 flex items-center justify-between gap-3 border-t border-aera-lightgray pt-2.5"
              data-testid="fw-advice-book-impact"
            >
              <span className="text-[11px] text-aera-gray">เงินตามสมุด</span>
              <span className="text-xs font-semibold tabular-nums text-aera-charcoal">
                {bahtLabelFromMajor(bookBalanceMajor!)}{" "}
                <span className="font-medium text-aera-amber">→</span>{" "}
                {bahtLabelFromMajor(bookAfterMajor!)}
              </span>
            </div>
          ) : !isAsk && !showWaitingSlipPick && recImpact ? (
            <p className="mt-2 text-right text-[11px] text-aera-gray">
              {recImpact}
            </p>
          ) : null}
        </section>
      ) : null}

      {!closed ? (
      <FinancialWorkEvidenceShell
        summary={card.evidenceSummary}
        items={matchEvidence}
        comparisonNote={showCompareBadge ? compareInsight : undefined}
        compare={showCompareBadge}
        caution={card.surfaceState === "risk"}
        settled={
          closed ||
          card.surfaceState === "closed"
        }
        focusObjectId={fromBatch ? card.objectRef.id : undefined}
        focusLabel={fromBatch ? titleText : undefined}
        onOpenDataNav={props.onOpenDataNav}
      />
      ) : null}

      {!closed && otherActions.length > 0 ? (
        <AeraAccordion
          ariaLabel="วิธีดำเนินการอื่น"
          triggerClassName="text-sm"
          triggerContentClassName="flex items-center justify-between gap-3 py-2"
          contentClassName="mt-2 flex flex-col gap-1.5"
          trigger={
            <>
            <span className="text-aera-gray">ต้องการเลือกวิธีอื่นไหม?</span>
            <span className="shrink-0 font-semibold text-aera-charcoal">
              เปลี่ยนวิธี
            </span>
            </>
          }
        >
          {otherActions.map((action) => (
            <button
              key={action.actionId}
              type="button"
              className="w-full cursor-pointer rounded-xl bg-aera-warmcream px-4 py-3 text-center text-sm font-semibold text-aera-charcoal transition-colors hover:bg-aera-amber/20 disabled:cursor-not-allowed disabled:opacity-50"
              data-testid="fw-other-action"
              disabled={!canMutate}
              title={!canMutate ? blockedReason : undefined}
              onClick={() => props.onAction(action)}
            >
              {action.label || "ทำรายการ"}
            </button>
          ))}
        </AeraAccordion>
      ) : null}

      <AeraAccordion
        ariaLabel="ข้อมูลต้นทางและประวัติ"
        defaultOpen={provenanceDefaultOpen(card, closed)}
        triggerClassName="items-start border-t border-aera-lightgray py-5"
        triggerContentClassName="flex items-start gap-2"
        contentClassName="space-y-2 pb-1"
        trigger={
          <>
            <Info className="mt-0.5 h-4 w-4 shrink-0 text-aera-gray" strokeWidth={2} aria-hidden />
            <span className="min-w-0 flex-1">
              <span className="block text-sm font-medium text-aera-amberdark">
                ข้อมูลต้นทางและประวัติ
              </span>
              {provenanceSourcePreview !== "—" ? (
                <span
                  className="mt-0.5 block text-xs text-aera-gray"
                  data-testid="fw-provenance-source-preview"
                >
                  {provenanceSourcePreview}
                </span>
              ) : null}
            </span>
          </>
        }
      >
        <FinancialWorkProvenancePanel
          card={card}
          workspaceId={props.workspaceId}
          onOpenDataNav={props.onOpenDataNav}
        />
      </AeraAccordion>

      {fromBatch ? (
        <>
          <FinancialWorkBacklogChildrenPanel
            childrenRows={unpaidBatchChildren}
            outstandingSummary={card.outstanding}
            currentObjectId={card.objectRef.id}
            heading="ใครยังไม่จ่าย"
            onOpenChild={props.onOpenChild}
          />
          <FinancialWorkBacklogChildrenPanel
            childrenRows={paidBatchChildren}
            heading="ยอดที่จับคู่แล้ว"
            countText={`${paidBatchChildren.length} ราย`}
            onOpenChild={props.onOpenChild}
          />
        </>
      ) : isAsk ? null : closed ? null : (
        <>
          <FinancialWorkOutstandingPanel
            outstanding={card.outstanding}
            onOpenChild={props.onOpenChild}
            onAction={props.onAction}
          />
          {isBatch ? (
            <FinancialWorkBacklogChildrenPanel
              childrenRows={batchChildren.filter((row) => isBacklogChildPaid(row))}
              heading="ยอดที่จับคู่แล้ว"
              countText={`${batchChildren.filter((row) => isBacklogChildPaid(row)).length} ราย`}
              onOpenChild={props.onOpenChild}
            />
          ) : null}
        </>
      )}

      {primary && !embeddedInModal ? (
        <FinancialWorkStickyAction
          label={primary.label || "ทำรายการ"}
          disabled={
            !canMutate ||
            recovery.kind === "submitting" ||
            recovery.kind === "processing"
          }
          disabledReason={!canMutate ? blockedReason : undefined}
          onClick={() => props.onAction(primary)}
        />
      ) : null}
    </div>
  );
}

function backlogChildStatusLabel(status: BacklogChildRow["status"]): string {
  switch (status) {
    case "open":
      return "ค้าง";
    case "waiting_slip":
      return "รอสลิป";
    case "paid":
      return "จ่ายแล้ว";
    case "needs_review":
      return "ต้องตรวจ";
    default:
      return status;
  }
}

function backlogChildMeta(row: BacklogChildRow): string {
  const status = backlogChildStatusLabel(row.status);
  const slip = row.slipLabel?.trim() || "";
  if (!slip || slip === status) return status;
  return `${status} · ${slip}`;
}

async function copyReminderDraft(
  message: string,
  onCopied: (ok: boolean) => void,
) {
  try {
    await navigator.clipboard.writeText(message);
    onCopied(true);
    showToast("คัดลอกร่างแล้ว");
    window.setTimeout(() => onCopied(false), 1500);
  } catch {
    onCopied(false);
    showToast("คัดลอกไม่สำเร็จ ลองอีกครั้ง", "error");
  }
}

function childSlipThumbUrl(row: BacklogChildRow, workspaceId: string): string {
  const direct = row.slipImageUrl?.trim() || "";
  if (direct) return direct;
  // Only income-record ids have /transactions/{id}/image — receivable order ids 404.
  if (row.dataNav?.workerId !== "bill-lin-income") return "";
  const recordId = row.dataNav?.recordId?.trim() || "";
  if (!recordId || !workspaceId) return "";
  return `/api/v1/transactions/${encodeURIComponent(recordId)}/image?workspace_id=${encodeURIComponent(workspaceId)}`;
}

function PersonRowThumb({ row }: { row: BacklogChildRow }) {
  const searchParams = useSearchParams();
  const workspaceId = (
    searchParams?.get("workspaceId") ||
    searchParams?.get("workspace_id") ||
    ""
  ).trim();
  const url = childSlipThumbUrl(row, workspaceId);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    setFailed(false);
  }, [url]);

  if (!url || failed) {
    return (
      <NoImageThumb
        className="h-10 w-10 shrink-0 rounded-lg"
        iconClassName="h-4 w-4"
      />
    );
  }

  return (
    // eslint-disable-next-line @next/next/no-img-element
    <img
      src={url}
      alt=""
      className="h-10 w-10 shrink-0 rounded-lg object-cover"
      onError={() => setFailed(true)}
    />
  );
}

function ReminderDraftActions(props: {
  sendApproved?: boolean;
}) {
  if (!props.sendApproved) return null;
  return (
    <AeraButton
      type="button"
      variant="outline"
      size="xs"
      className="cursor-pointer"
      data-testid="fw-reminder-send"
      onClick={() => {
        showToast("ยังไม่มีช่องทางส่งที่อนุมัติจากเซิร์ฟเวอร์ — คัดลอกร่างไปส่งเอง");
      }}
    >
      ส่ง
    </AeraButton>
  );
}

/** R0-05: one outstanding child + optional draft/copy (never a second name list). */
function OutstandingPersonRow(props: {
  row: BacklogChildRow;
  draft?: OutstandingReminderDraft;
  onOpenChild?: (objectRef: { kind: string; id: string }) => void;
  onAction?: (action: ActionDirective) => void;
}) {
  const { row, draft } = props;
  const [expanded, setExpanded] = useState(false);
  const [copied, setCopied] = useState(false);
  const matchFacts = childMatchFactsItem(row);
  const { canMutate, blockedReason } = useFinancialWorkMutationGate();
  return (
    <li
      className="border-b border-aera-lightgray/70 px-3 py-2.5 last:border-b-0"
      data-testid="fw-outstanding-item"
    >
      <div className="flex items-center gap-2">
        <PersonRowThumb row={row} />
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-medium text-aera-charcoal">
            {row.label}
          </p>
          <p className="truncate text-[11px] text-aera-gray">
            {backlogChildMeta(row)}
          </p>
        </div>
        {row.remainingAmountMinor != null ? (
          <p className="shrink-0 text-sm font-semibold tabular-nums text-aera-charcoal">
            {formatAmountMinor(row.remainingAmountMinor, row.currency || "THB")}
          </p>
        ) : null}
        {props.onOpenChild ? (
          <AeraButton
            type="button"
            variant="secondary"
            size="xs"
            className="shrink-0 cursor-pointer px-2.5!"
            data-testid="fw-open-child"
            disabled={!canMutate}
            title={!canMutate ? blockedReason : undefined}
            onClick={() => props.onOpenChild?.(row.objectRef)}
          >
            ดู
          </AeraButton>
        ) : null}
      </div>
      {matchFacts ? (
        <div className="mt-1.5 pl-12">
          <CounterpartMatchFacts item={matchFacts} />
        </div>
      ) : null}
      {draft ? (
        <div className="mt-2" data-testid="fw-reminder-draft">
          <button
            type="button"
            className="cursor-pointer text-[11px] font-medium text-aera-gray underline-offset-2 hover:underline"
            aria-expanded={expanded}
            onClick={() => setExpanded((v) => !v)}
          >
            {expanded ? "ซ่อนร่าง" : "ดูร่างทวง"}
          </button>
          <div
            className={[
              "grid transition-[grid-template-rows,opacity] duration-300 ease-out",
              expanded
                ? "grid-rows-[1fr] opacity-100"
                : "pointer-events-none grid-rows-[0fr] opacity-0",
            ].join(" ")}
            aria-hidden={!expanded}
          >
            <div className="min-h-0 overflow-hidden">
              <div className="mt-1.5">
                <ReminderToneDrafts
                  polite={draft.messageDraft}
                  direct={draft.messageDraftDirect}
                  copied={copied}
                  onCopy={(message) => {
                    void copyReminderDraft(message, setCopied);
                    if (draft.copyAction) props.onAction?.(draft.copyAction);
                  }}
                />
                <ReminderDraftActions sendApproved={draft.sendApproved} />
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </li>
  );
}

function DebtorRow(props: {
  row: BacklogChildRow;
  testId: string;
  current?: boolean;
  onOpenChild?: (objectRef: { kind: string; id: string }) => void;
}) {
  const { row } = props;
  const matchFacts = childMatchFactsItem(row);
  const { canMutate, blockedReason } = useFinancialWorkMutationGate();
  return (
    <li
      className="border-b border-aera-lightgray/70 px-3 py-2 last:border-b-0"
      data-testid={props.testId}
      data-current={props.current ? "true" : "false"}
    >
      <div className="flex min-h-11 items-center gap-2">
        <PersonRowThumb row={row} />
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-medium text-aera-charcoal">
            {row.label}
            {props.current ? (
              <span className="ml-1.5 text-[11px] font-normal text-aera-gray">
                · รายการนี้
              </span>
            ) : null}
          </p>
          <p className="truncate text-[11px] text-aera-gray">
            {backlogChildMeta(row)}
          </p>
        </div>
        {childClosedAmountMinor(row) != null ? (
          <p
            className={[
              "shrink-0 text-sm font-semibold tabular-nums",
              row.status === "paid" ? "text-aera-gray" : "text-aera-charcoal",
            ].join(" ")}
          >
            {formatAmountMinor(childClosedAmountMinor(row)!, row.currency || "THB")}
          </p>
        ) : null}
        {!props.current && props.onOpenChild ? (
          <AeraButton
            type="button"
            variant="secondary"
            size="xs"
            className="shrink-0 cursor-pointer px-2.5!"
            data-testid="fw-open-child"
            disabled={!canMutate}
            title={!canMutate ? blockedReason : undefined}
            onClick={() => props.onOpenChild?.(row.objectRef)}
          >
            ดู
          </AeraButton>
        ) : null}
      </div>
      {matchFacts ? (
        <div className="mt-1.5 pl-12">
          <CounterpartMatchFacts item={matchFacts} />
        </div>
      ) : null}
    </li>
  );
}

/** Compact round totals for overview — no per-person rows (avoids duplicating chase). */
export function FinancialWorkRoundSummary(props: {
  childrenRows?: BacklogChildRow[];
  outstanding?: OutstandingSlice;
}) {
  const rows = props.childrenRows ?? [];
  const openCount =
    props.outstanding?.openCount ??
    rows.filter((r) => r.status === "open" || r.status === "waiting_slip")
      .length;
  if (rows.length === 0 && !props.outstanding) return null;

  const totalLabel =
    rows.length > 0 ? `${rows.length} รายในรอบ` : `${openCount} รายค้าง`;
  const amount =
    props.outstanding?.remainingAmountMinor != null
      ? formatAmountMinor(
          props.outstanding.remainingAmountMinor,
          props.outstanding.currency || "THB",
        )
      : null;

  return (
    <section className="space-y-1.5" data-testid="fw-round-summary">
      <h3 className="text-xs font-semibold text-aera-gray">สรุปรอบ</h3>
      <div className="rounded-2xl border border-aera-lightgray/80 bg-aera-white px-3 py-3">
        <p className="text-sm text-aera-charcoal">
          {totalLabel}
          {openCount > 0 ? (
            <span className="text-aera-gray"> · ยังค้าง {openCount}</span>
          ) : (
            <span className="text-aera-gray"> · ไม่มียอดค้างเปิด</span>
          )}
        </p>
        {amount ? (
          <p className="mt-0.5 text-sm font-semibold tabular-nums text-aera-charcoal">
            {amount}
          </p>
        ) : null}
      </div>
    </section>
  );
}

export function FinancialWorkBacklogChildrenPanel(props: {
  childrenRows?: BacklogChildRow[];
  outstandingSummary?: OutstandingSlice;
  highlighted?: boolean;
  currentObjectId?: string;
  heading?: string;
  countText?: string;
  onOpenChild?: (objectRef: { kind: string; id: string }) => void;
}) {
  const rows = props.childrenRows ?? [];
  if (rows.length === 0) return null;
  const openCount =
    props.outstandingSummary?.openCount ??
    rows.filter((r) => r.status === "open" || r.status === "waiting_slip")
      .length;

  return (
    <section className="space-y-1.5" data-testid="fw-backlog-children">
      <div className="flex items-baseline justify-between gap-2 px-0.5">
        <h3 className="text-xs font-semibold text-aera-gray">
          {props.heading || "ใครยังไม่จ่าย"}
        </h3>
        <p className="text-[11px] tabular-nums text-aera-gray">
          {props.countText || `${rows.length} ราย · ค้าง ${openCount}`}
        </p>
      </div>
      <ul
        className={[
          "overflow-hidden rounded-2xl border border-aera-lightgray/80 bg-aera-white",
          props.highlighted ? "ring-2 ring-aera-charcoal/15" : "",
        ].join(" ")}
      >
        {rows.map((row) => (
          <DebtorRow
            key={`${row.objectRef.kind}:${row.objectRef.id}`}
            row={row}
            testId="fw-backlog-child"
            current={row.objectRef.id === props.currentObjectId}
            onOpenChild={props.onOpenChild}
          />
        ))}
      </ul>
    </section>
  );
}

export function FinancialWorkReminderDraftsSection(props: {
  drafts: OutstandingReminderDraft[];
  onAction?: (action: ActionDirective) => void;
}) {
  if (props.drafts.length === 0) return null;
  return (
    <section className="space-y-1.5" data-testid="fw-reminder-drafts">
      <div className="space-y-0.5 px-0.5">
        <h3 className="text-xs font-semibold text-aera-gray">
          เตรียมข้อความทวง
        </h3>
        <p className="text-[11px] text-aera-gray">
          คัดลอกร่างไปส่งเอง — ระบบไม่ส่งข้อความให้อัตโนมัติ
        </p>
      </div>
      <ul className="space-y-2">
        {props.drafts.map((draft) => (
          <ReminderDraftCard
            key={`${draft.objectRef.kind}:${draft.objectRef.id}`}
            draft={draft}
            onAction={props.onAction}
          />
        ))}
      </ul>
    </section>
  );
}

function ReminderDraftCard(props: {
  draft: OutstandingReminderDraft;
  onAction?: (action: ActionDirective) => void;
}) {
  const [copied, setCopied] = useState(false);
  const [expanded, setExpanded] = useState(false);
  return (
    <li
      className="rounded-2xl border border-aera-lightgray/80 bg-aera-white px-3 py-2.5"
      data-testid="fw-reminder-draft"
    >
      <button
        type="button"
        className="flex w-full cursor-pointer items-center justify-between gap-2 text-left"
        onClick={() => setExpanded((v) => !v)}
        aria-expanded={expanded}
      >
        <p className="min-w-0 truncate text-sm font-medium text-aera-charcoal">
          {props.draft.recipientLabel}
        </p>
        <span className="shrink-0 text-[11px] text-aera-gray">
          {expanded ? "ซ่อน" : "ดูร่าง"}
        </span>
      </button>
      <div
        className={[
          "grid transition-[grid-template-rows,opacity] duration-300 ease-out",
          expanded ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0",
        ].join(" ")}
        aria-hidden={!expanded}
      >
        <div className="min-h-0 overflow-hidden">
          <div className="mt-2">
            <ReminderToneDrafts
              polite={props.draft.messageDraft}
              direct={props.draft.messageDraftDirect}
              copied={copied}
              onCopy={(message) => {
                void copyReminderDraft(message, setCopied);
                if (props.draft.copyAction) props.onAction?.(props.draft.copyAction);
              }}
            />
            <ReminderDraftActions sendApproved={props.draft.sendApproved} />
          </div>
        </div>
      </div>
    </li>
  );
}

export function FinancialWorkOutstandingPanel(props: {
  outstanding?: OutstandingSlice;
  highlighted?: boolean;
  hideTitle?: boolean;
  onOpenChild?: (objectRef: { kind: string; id: string }) => void;
  onAction?: (action: ActionDirective) => void;
}) {
  const slice = props.outstanding;
  if (!slice) return null;
  return (
    <section className="space-y-3" data-testid="fw-outstanding-panel">
      <div className="space-y-1.5">
        {props.hideTitle ? null : (
          <div className="flex items-baseline justify-between gap-2 px-0.5">
            <h3 className="text-sm font-semibold text-aera-charcoal">
              ใครยังไม่จ่าย
            </h3>
            <p className="text-[11px] tabular-nums text-aera-gray">
              {slice.openCount} ราย
              {slice.remainingAmountMinor != null
                ? ` · ${formatAmountMinor(
                    slice.remainingAmountMinor,
                    slice.currency || "THB",
                  )}`
                : ""}
            </p>
          </div>
        )}
        {slice.items.length === 0 ? (
          <p className="rounded-2xl border border-dashed border-aera-lightgray bg-aera-offwhite px-3 py-2.5 text-xs text-aera-gray">
            ไม่มีรายการค้างเปิดจาก projection ปัจจุบัน
          </p>
        ) : (
          <ul
            className={[
              "overflow-hidden rounded-2xl border border-aera-lightgray/80 bg-aera-white",
              props.highlighted ? "ring-2 ring-aera-charcoal/15" : "",
            ].join(" ")}
          >
            {slice.items.map((row) => {
              const draft = slice.reminderDrafts?.find(
                (d) =>
                  d.objectRef.kind === row.objectRef.kind &&
                  d.objectRef.id === row.objectRef.id,
              );
              return (
                <OutstandingPersonRow
                  key={`${row.objectRef.kind}:${row.objectRef.id}`}
                  row={row}
                  draft={draft}
                  onOpenChild={props.onOpenChild}
                  onAction={props.onAction}
                />
              );
            })}
          </ul>
        )}
      </div>
    </section>
  );
}

/** Standalone outstanding modal — same `Modal` chrome as รับช่วงยอดค้าง. */
export function FinancialWorkOutstandingSheet(props: {
  open: boolean;
  card: LiffFinancialWorkCard | null;
  onClose: () => void;
  onAction: (action: ActionDirective) => void;
  onOpenChild?: (objectRef: { kind: string; id: string }) => void;
  onReload?: () => void;
  preventBackdropClose?: boolean;
}) {
  const { canMutate, blockedReason } = useFinancialWorkMutationGate();
  const card = props.card;
  const slice = card?.outstanding;
  const isOpen = props.open && Boolean(slice);
  const primary = card?.availableActions.find(
    (a) =>
      a.actionType !== "open_detail" &&
      a.actionType !== "open_outstanding" &&
      a.actionType !== "open_data",
  );
  const amount =
    slice?.remainingAmountMinor != null
      ? formatAmountMinor(
          slice.remainingAmountMinor,
          slice.currency || "THB",
        )
      : null;

  return (
    <Modal
      isOpen={isOpen}
      onClose={props.onClose}
      size="md"
      placement="responsive-drawer"
      sheetHost
      panelClassName={props.preventBackdropClose ? "!overflow-hidden" : undefined}
      preventBackdropClose={props.preventBackdropClose}
      zIndexClass="z-[10060]"
      bodyClassName="bg-aera-offwhite"
      footerClassName="pb-[max(1rem,env(safe-area-inset-bottom))]"
      title={
        <span className="flex items-center gap-2">
          <Users className="h-5 w-5 shrink-0" strokeWidth={2} aria-hidden />
          ใครยังไม่จ่าย
        </span>
      }
      footer={
        primary ? (
          <div className="w-full">
            {!canMutate ? (
              <p className="mb-1.5 text-center text-[11px] text-aera-gray">
                {blockedReason}
              </p>
            ) : null}
            <AeraButton
              type="button"
              variant="primary"
              size="md"
              className="w-full"
              disabled={!canMutate}
              title={!canMutate ? blockedReason : undefined}
              data-testid="fw-outstanding-sheet-action"
              onClick={() => props.onAction(primary)}
            >
              {primary.label || "ทำรายการ"}
            </AeraButton>
          </div>
        ) : undefined
      }
    >
      <div data-testid="fw-outstanding-sheet" className="min-w-0">
        <p className="text-sm text-aera-gray">
          {displayWorkTitle(card?.title) || "ชุดยอดค้างนี้"}
          {amount ? (
            <>
              {" "}
              <span className="text-aera-gray">•</span> {amount}
            </>
          ) : null}
          {slice ? (
            <>
              {" "}
              <span className="text-aera-gray">•</span> {slice.openCount} ราย
            </>
          ) : null}
        </p>
        <p className="mt-2 text-sm leading-relaxed text-aera-gray">
          รายที่ยังค้างในรอบนี้ — คัดลอกร่างทวงไปส่งเองได้{" "}
          <span className="font-semibold text-aera-charcoal">
            ระบบไม่ส่งข้อความให้อัตโนมัติ
          </span>
        </p>
        {props.onReload ? (
          <AeraButton
            type="button"
            variant="outline"
            size="xs"
            className="mt-2 cursor-pointer"
            data-testid="fw-outstanding-refresh"
            onClick={props.onReload}
          >
            โหลดรายชื่อล่าสุด
          </AeraButton>
        ) : null}
        {slice && slice.items.length > 0 ? (
          <ul
            className="mt-3 overflow-hidden rounded-lg border border-aera-lightgray/80 bg-aera-offwhite/50"
            data-testid="fw-outstanding-panel"
          >
            {slice.items.map((row) => {
              const draft = slice.reminderDrafts?.find(
                (d) =>
                  d.objectRef.kind === row.objectRef.kind &&
                  d.objectRef.id === row.objectRef.id,
              );
              return (
                <OutstandingPersonRow
                  key={`${row.objectRef.kind}:${row.objectRef.id}`}
                  row={row}
                  draft={draft}
                  onOpenChild={props.onOpenChild}
                  onAction={props.onAction}
                />
              );
            })}
          </ul>
        ) : (
          <p className="mt-3 text-sm text-aera-gray">ไม่มีรายการค้างเปิด</p>
        )}
      </div>
    </Modal>
  );
}

const FW_PARENT_PANEL_SELECTOR = "[data-aera-modal-panel]";

function useMinWidthMd() {
  const [isDesktop, setIsDesktop] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia("(min-width: 768px)");
    const apply = () => setIsDesktop(mq.matches);
    apply();
    mq.addEventListener("change", apply);
    return () => mq.removeEventListener("change", apply);
  }, []);
  return isDesktop;
}

/** Nested child peek — same stacking as record cancel confirm over its parent modal. */
export function FinancialWorkChildOverlay(props: {
  open: boolean;
  card: LiffFinancialWorkCard | null;
  onClose: () => void;
  onOpenDataNav?: (nav: {
    workerId: string;
    recordId?: string;
    scrollTo?: string;
    highlight?: string;
  }) => void;
}) {
  const isDesktop = useMinWidthMd();
  const { canMutate, blockedReason } = useFinancialWorkMutationGate();
  const searchParams = useSearchParams();
  const imageWorkspaceId = (
    searchParams?.get("workspaceId") ||
    searchParams?.get("workspace_id") ||
    ""
  ).trim();
  const imageOwnerUserId = (
    searchParams?.get("owner_user_id") ||
    searchParams?.get("ownerUserId") ||
    getUserIdFromAuthToken() ||
    ""
  ).trim();
  const [scopedHost, setScopedHost] = useState<Element | null>(null);
  const [hostChecked, setHostChecked] = useState(false);
  const card = props.card;
  const { data: incomeRecord } = useFinancialWorkIncomeRecord(
    card,
    imageWorkspaceId,
  );
  const incomeRecordTarget = financialWorkIncomeRecordTarget(card);
  const incomeTransferMinor = incomeRecord
    ? readIncomeTransferMinor(incomeRecord)
    : 0;
  const evidenceItems = card
    ? comparisonEvidenceItems(
        card,
        imageWorkspaceId,
        imageOwnerUserId,
      ).map((item) =>
        item.transferorLabel &&
        item.transferAmountMinor == null &&
        item.dataNav?.recordId === incomeRecordTarget?.recordId &&
        incomeTransferMinor > 0
          ? { ...item, transferAmountMinor: incomeTransferMinor }
          : item,
      )
    : [];
  const isOpen = props.open && Boolean(card);
  const amount = card?.amountSummary
    ? formatAmountMinor(
        card.amountSummary.primaryAmountMinor,
        card.amountSummary.currency,
      )
    : null;

  useLayoutEffect(() => {
    if (!isOpen) {
      setHostChecked(false);
      return;
    }
    setScopedHost(document.querySelector(FW_PARENT_PANEL_SELECTOR));
    setHostChecked(true);
  }, [isOpen, isDesktop]);

  const isScoped = scopedHost != null;
  if (isOpen && !hostChecked) return null;

  return (
    <ModalShell
      isOpen={isOpen}
      onClose={props.onClose}
      onExited={() => setScopedHost(null)}
      align="bottom"
      scopedHost={isScoped ? scopedHost : null}
      preventBackdropClose={false}
      zIndexClass="z-[10070]"
      overlayClassName={
        isScoped ? "bg-aera-black/40" : undefined
      }
      panelClassName={[
        "flex flex-col overflow-hidden rounded-t-3xl rounded-b-none border-t border-aera-lightgray bg-aera-white shadow-xl",
        isScoped ? "max-h-[85%]" : "max-h-[80dvh]",
      ].join(" ")}
    >
      <div data-testid="fw-child-overlay">
        <div className="flex shrink-0 justify-center pb-1 pt-2">
          <div className="h-1 w-10 rounded-full bg-aera-lightgray" aria-hidden />
        </div>
        <header className="flex shrink-0 items-start justify-between gap-3 px-4 pb-4">
          <div className="min-w-0">
            <h2 className="text-xl font-bold text-aera-charcoal">
              {displayWorkTitle(card?.title) || "รายการนี้"}
            </h2>
            <p className="mt-0.5 text-sm text-aera-gray">
              {card ? workStatusLabel(card) : ""}
              {amount ? (
                <>
                  {" "}
                  <span className="text-aera-gray">•</span> {amount}
                </>
              ) : null}
            </p>
          </div>
          <ModalCloseButton
            onClose={props.onClose}
            className="-mr-1 shrink-0"
          />
        </header>
        <div className="min-h-0 flex-1 overflow-y-auto px-4 py-3 text-aera-charcoal">
          <p className="text-sm leading-relaxed text-aera-gray">
            {card?.nextStep.label || "ดูรายละเอียดรายการในชุดนี้"}
          </p>
          {card?.contextRows?.filter((row) => !isHiddenWorkContextRow(row))
            .length ? (
            <dl className="mt-3 space-y-1.5 rounded-lg border border-aera-lightgray/80 bg-aera-offwhite/50 px-3 py-2.5">
              {card.contextRows
                .filter((row) => !isHiddenWorkContextRow(row))
                .map((row) => (
                <div
                  key={row.key}
                  className="flex justify-between gap-3 text-sm"
                >
                  <dt className="shrink-0 text-aera-gray">{row.label}</dt>
                  <dd className="min-w-0 text-right text-aera-charcoal">
                    {row.value}
                  </dd>
                </div>
              ))}
            </dl>
          ) : null}
          <div className="mt-3 space-y-3">
            <FinancialWorkTimeline events={card?.timelineEvents} />
            {card && card.workPlacement.bucket !== "recently_closed" ? (
              <FinancialWorkEvidenceShell
                summary={card.evidenceSummary}
                items={evidenceItems}
                compare={
                  !card.requirementStatus.complete &&
                  (isSlipOwnerAskCard(card) ||
                    card.evidenceSummary.needsReview > 0 ||
                    card.surfaceState === "needs_review" ||
                    card.surfaceState === "risk")
                }
                caution={card.surfaceState === "risk"}
                settled={card.requirementStatus.complete || card.surfaceState === "closed"}
                onOpenDataNav={props.onOpenDataNav}
              />
            ) : null}
          </div>
        </div>
        <div className="shrink-0 space-y-3 px-4 pt-3 pb-[max(0.75rem,env(safe-area-inset-bottom))]">
          {props.onOpenDataNav && card ? (
            <AeraButton
              type="button"
              variant="primary"
              fontWeight="medium"
              fullWidth
              className="cursor-pointer"
              data-testid="fw-child-open-data"
              disabled={!canMutate}
              title={!canMutate ? blockedReason : undefined}
              onClick={(event) => {
                event.preventDefault();
                event.stopPropagation();
                const fromEvidence = card.evidenceItems?.find(
                  (item) => item.dataNav?.workerId === "bill-lin-receivable",
                )?.dataNav;
                props.onOpenDataNav?.(
                  fromEvidence ?? {
                    workerId: "bill-lin-receivable",
                    scrollTo: "receivable",
                    recordId: card.objectRef.id.replace(/^redis:/, ""),
                    highlight: card.objectRef.id,
                  },
                );
              }}
            >
              แก้ไขยอด
            </AeraButton>
          ) : null}
          <AeraButton
            type="button"
            variant="outline"
            fontWeight="medium"
            fullWidth
            className="cursor-pointer"
            data-testid="fw-child-overlay-dismiss"
            onClick={(event) => {
              event.preventDefault();
              event.stopPropagation();
              props.onClose();
            }}
          >
            ปิด
          </AeraButton>
        </div>
      </div>
    </ModalShell>
  );
}

function FinancialWorkTimeline(props: { events?: WorkTimelineEvent[] }) {
  const events = props.events ?? [];
  if (events.length === 0) return null;
  return (
    <p className="text-xs text-aera-gray" data-testid="fw-timeline">
      {events.map((event, i) => (
        <span key={event.id} data-testid="fw-timeline-event">
          {i > 0 ? " · " : ""}
          {event.label}
        </span>
      ))}
    </p>
  );
}

function NoImageThumb({
  className,
  iconClassName,
}: {
  className?: string;
  iconClassName?: string;
}) {
  return (
    <span
      className={[
        "flex items-center justify-center overflow-hidden bg-aera-lightgray text-aera-lightslate",
        className,
      ]
        .filter(Boolean)
        .join(" ")}
      aria-label="ไม่มีรูป"
      title="ไม่มีรูป"
      data-testid="fw-no-image"
    >
      <ImageIcon
        className={iconClassName ?? "h-4 w-4"}
        strokeWidth={1.75}
        aria-hidden
      />
    </span>
  );
}

function EvidenceLedgerThumb(props: { onClick?: () => void }) {
  const box = (
    <span
      className="flex h-[52px] w-[52px] shrink-0 flex-col items-center justify-center gap-0.5 rounded-lg border border-aera-lightgray bg-aera-offwhite px-1 py-1.5 text-center"
      data-testid="fw-evidence-ledger-thumb"
    >
      <Database className="h-4 w-4 shrink-0 text-aera-gray" strokeWidth={1.75} aria-hidden />
      <span className="text-[8px] font-medium leading-tight text-aera-gray">
        ข้อมูลรายการ
      </span>
    </span>
  );
  if (!props.onClick) return box;
  return (
    <button type="button" className="cursor-pointer" onClick={props.onClick}>
      {box}
    </button>
  );
}

function EvidenceThumb(props: {
  src: string;
  alt: string;
  onOpen: () => void;
}) {
  const [failed, setFailed] = useState(false);
  const url = props.src.trim();

  useEffect(() => {
    setFailed(false);
  }, [url]);

  if (!url || failed) {
    return (
      <span
        className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border border-aera-lightgray bg-aera-offwhite text-aera-gray"
        aria-hidden
      >
        <Database className="h-4 w-4" strokeWidth={1.75} />
      </span>
    );
  }

  return (
    <button
      type="button"
      className="relative h-10 w-10 shrink-0 overflow-hidden rounded-lg border border-aera-lightgray bg-aera-white"
      data-testid="fw-evidence-thumb"
      onClick={props.onOpen}
    >
      <AeraImage
        src={url}
        alt={props.alt}
        className="h-full w-full object-cover"
        native
        onError={() => setFailed(true)}
      />
    </button>
  );
}

function formatEvidenceDatetime(iso: string | undefined): string {
  const raw = (iso || "").trim();
  if (!raw) return "";
  const dateLabel = formatThaiDateCard(raw);
  if (!dateLabel || dateLabel === "—") return "";
  const parsed = parseDatetimeInput(raw);
  if (!parsed) return dateLabel;
  const { timeLabel } = formatThaiDatetimePartsFromDate(parsed);
  return timeLabel ? `${dateLabel} ${timeLabel} น.` : dateLabel;
}

function formatEvidenceTime(iso: string | undefined): string {
  const parsed = parseDatetimeInput((iso || "").trim());
  if (!parsed) return "";
  const { timeLabel } = formatThaiDatetimePartsFromDate(parsed);
  return timeLabel ? `${timeLabel} น.` : "";
}

function EvidenceFact({ children }: { children: ReactNode }) {
  return (
    <p className="break-words text-xs leading-relaxed text-aera-gray">
      {children}
    </p>
  );
}

function EvidenceAmountRow({
  label,
  amount,
  dot,
}: {
  label: string;
  amount: string;
  dot?: boolean;
}) {
  return (
    <p className="flex flex-wrap items-baseline gap-x-1 text-xs leading-relaxed text-aera-gray">
      <span>{dot ? `${label} ·` : label}</span>
      <span className="font-medium tabular-nums text-aera-charcoal">{amount}</span>
    </p>
  );
}

function CounterpartMatchFacts({ item }: { item: EvidenceItem }) {
  const matched =
    item.matchedAmountMinor != null
      ? formatAmountMinor(item.matchedAmountMinor, "THB")
      : item.counterpartDetail?.trim() || "";
  const dateLabel = formatEvidenceDatetime(item.occurredAt);
  const outstandingMinor =
    item.outstandingAmountMinor ?? item.originalAmountMinor ?? null;

  return (
    <div
      className="mt-1 space-y-1"
      data-testid="fw-evidence-counterpart"
    >
      {dateLabel ? (
        <p className="flex flex-wrap items-baseline gap-x-1 text-xs leading-relaxed text-aera-gray">
          <span>วันที่ทำรายการ ·</span>
          <span>{dateLabel}</span>
        </p>
      ) : null}
      <div className="flex flex-wrap gap-1">
      {outstandingMinor != null ? (
        <EvidenceAmountRow
          label="ยอดค้าง"
          amount={formatAmountMinor(outstandingMinor)}
        />
      ) : null}
      {matched ? (
        <EvidenceAmountRow label="จับคู่กับสลิปนี้" amount={matched} dot />
      ) : (
        <EvidenceFact>จับคู่กับสลิปแล้ว</EvidenceFact>
      )}
      {item.remainingAmountMinor != null ? (
        <EvidenceAmountRow
          label="เหลือ"
          amount={formatAmountMinor(item.remainingAmountMinor)}
        />
      ) : null}
      </div>
    </div>
  );
}

function EvidenceMatchRow(props: {
  item: EvidenceItem;
  imageUrl: string;
  timeline?: boolean;
  timelineConnectAbove?: boolean;
  timelineConnectBelow?: boolean;
  railTone?: "amber" | "red";
  settled?: boolean;
  onPreview: (url: string, caption: string) => void;
  onOpenDataNav?: (nav: {
    workerId: string;
    recordId?: string;
    scrollTo?: string;
    highlight?: string;
  }) => void;
}) {
  const { item, imageUrl, timeline } = props;
  const { canMutate, blockedReason } = useFinancialWorkMutationGate();
  const showPair = !timeline && Boolean(item.counterpartLabel);
  const slipDate = formatEvidenceDatetime(item.occurredAt);
  const slipTime = formatEvidenceTime(item.occurredAt);
  const dueAt = formatEvidenceDatetime(item.expiresAt);
  const matchedAt = formatEvidenceDatetime(item.matchedAt);
  const amountDetail = item.detail?.match(/^(ยอดโอน|ยอดค้าง|ยอด)\s+(.+)$/);
  const transferAmount =
    (item.transferAmountMinor != null
      ? formatAmountMinor(item.transferAmountMinor)
      : item.transferAmountLabel) ||
    (amountDetail?.[1] === "ยอดโอน" ? amountDetail[2] : item.detail) ||
    undefined;
  const ledger = isLedgerEvidenceItem(item);
  const railBorder =
    props.railTone === "red" ? "border-aera-red" : "border-aera-amber";
  const railBg =
    props.railTone === "red" ? "bg-aera-red" : "bg-aera-amber";
  const timelineLineClass = () => {
    const { timelineConnectAbove, timelineConnectBelow } = props;
    if (!timelineConnectAbove && !timelineConnectBelow) return null;
    if (timelineConnectAbove && timelineConnectBelow) {
      return "top-0 bottom-0";
    }
    if (timelineConnectAbove) {
      return "top-0 h-[calc(0.75rem+0.875rem+0.3125rem)]";
    }
    return "top-[calc(0.75rem+0.875rem+0.3125rem)] bottom-0";
  };
  const openRow = () => {
    if (imageUrl) {
      props.onPreview(imageUrl, displayLabel);
      return;
    }
    if (item.dataNav && props.onOpenDataNav) {
      props.onOpenDataNav(item.dataNav);
    }
  };

  const sourceMeta = item.sourceLabel?.trim() || "";
  const displaySourceMeta = /^สลิปจาก LINE(?:\s*·|$)/i.test(sourceMeta)
    ? "LINE OA"
    : sourceMeta;
  const displayLabel =
    item.label === "สลิป" && sourceMeta
      ? /LINE/i.test(sourceMeta)
        ? "สลิปจาก LINE"
        : sourceMeta.split("·")[0]?.trim() || item.label
      : item.label;
  const meta = item.transferorLabel
    ? slipDate
      ? [displaySourceMeta, slipDate].filter(Boolean).join(" · ")
      : displaySourceMeta
    : slipDate
    ? item.sourceLabel?.trim()
      ? `${item.sourceLabel.trim()} · ${slipDate}`
      : slipDate
    : item.sourceLabel?.trim() || "";

  const thumb = ledger ? (
    <EvidenceLedgerThumb onClick={openRow} />
  ) : (
    <FinancialWorkDocThumb
      {...evidenceThumbForItem(item)}
      imageUrl={imageUrl}
      testId="fw-evidence-thumb"
      onClick={openRow}
    />
  );

  if (timeline) {
    const lineClass = timelineLineClass();
    return (
      <li
        className="relative"
        data-testid="fw-evidence-item"
        data-status={item.status}
        data-matched-object={item.matchedObjectId || undefined}
      >
        {lineClass ? (
          <span
            className={`pointer-events-none absolute left-[4px] w-0.5 ${railBg} ${lineClass}`}
            aria-hidden
          />
        ) : null}
        <div className="flex items-start gap-3 py-3">
          <span
            className={`relative z-10 mt-3.5 h-2.5 w-2.5 shrink-0 rounded-full border-2 bg-aera-white ${railBorder}`}
            aria-hidden
          />
          {thumb}
          <button
            type="button"
            className="min-w-0 flex-1 cursor-pointer space-y-0.5 text-left"
            onClick={openRow}
          >
            <span className="flex min-w-0 flex-wrap items-center gap-1.5">
              <span className="break-words text-sm font-semibold text-aera-charcoal">
                {displayLabel}
              </span>
            </span>
            {meta ? (
              <span className="block break-words text-xs text-aera-gray">{meta}</span>
            ) : null}
            {amountDetail || item.transferorLabel ? (
              <AeraTransferMeta
                amount={item.transferorLabel ? transferAmount : amountDetail?.[2]}
                amountLabel={
                  item.transferorLabel ? "ยอดโอน" : amountDetail?.[1]
                }
                matchedAmount={
                  props.settled && item.transferorLabel
                    ? item.matchedAmountLabel ||
                      (item.matchedAmountMinor != null
                        ? formatAmountMinor(item.matchedAmountMinor)
                        : undefined)
                    : undefined
                }
                transferor={item.transferorLabel}
                time={item.transferorLabel ? slipTime : undefined}
                dueAt={
                  !item.transferorLabel && amountDetail?.[1] !== "ยอดโอน"
                    ? dueAt
                    : undefined
                }
                matchedAt={
                  !item.transferorLabel && amountDetail?.[1] !== "ยอดโอน"
                    ? matchedAt
                    : undefined
                }
              />
            ) : item.detail ? (
              <span className="block break-words text-xs text-aera-gray">
                {item.detail}
              </span>
            ) : null}
          </button>
          <button
            type="button"
            className="mt-3 shrink-0 cursor-pointer text-aera-lightslate"
            aria-label={`เปิด${displayLabel}`}
            onClick={openRow}
          >
            <ChevronRight className="h-4 w-4" aria-hidden />
          </button>
        </div>
      </li>
    );
  }

  return (
    <li
      className="py-5"
      data-testid="fw-evidence-item"
      data-status={item.status}
      data-matched-object={item.matchedObjectId || undefined}
    >
      <div className="flex items-stretch gap-3">
        <div className="flex w-[52px] shrink-0 flex-col items-center">
          {thumb}
          {showPair ? (
            <span
              className="my-2 w-0 min-h-5 flex-1 border-l border-dashed border-aera-lightgray"
              aria-hidden
            />
          ) : null}
          {showPair ? (
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border border-aera-lightgray bg-aera-offwhite text-aera-gray">
              <Database className="h-4 w-4" strokeWidth={1.75} aria-hidden />
            </span>
          ) : null}
        </div>
        <div className="flex min-w-0 flex-1 flex-col gap-5">
          <div className="space-y-1">
            <div className="flex min-w-0 flex-wrap items-center gap-1.5">
              <p className="break-words text-sm font-semibold text-aera-charcoal">
                {displayLabel}
              </p>
            </div>
            {meta ? (
              <p className="break-words text-xs text-aera-gray">{meta}</p>
            ) : null}
            {item.detail ? (
              <p className="break-words text-xs text-aera-gray">
                {item.status === "complete" ? (
                  <>
                    ยอดโอน{" "}
                    <span className="font-medium tabular-nums text-aera-charcoal">
                      {amountDetail?.[1] === "ยอดโอน"
                        ? amountDetail[2]
                        : item.detail}
                    </span>
                  </>
                ) : (
                  item.detail
                )}
              </p>
            ) : null}
            {item.status === "complete" ? (
              <div className="flex flex-wrap gap-3 pt-0.5">
                {imageUrl ? (
                  <AeraButton
                    type="button"
                    variant="link"
                    size="xs"
                    className="h-auto min-h-0 cursor-pointer px-0 py-0 text-[11px] text-aera-gray"
                    data-testid="fw-evidence-preview"
                    onClick={() => props.onPreview(imageUrl, displayLabel)}
                  >
                    ดูสลิป
                  </AeraButton>
                ) : null}
                <AeraButton
                  type="button"
                  variant="link"
                  size="xs"
                  className="h-auto min-h-0 cursor-pointer px-0 py-0 text-[11px] text-aera-gray"
                  data-testid="fw-evidence-open-income"
                  disabled={!canMutate}
                  title={!canMutate ? blockedReason : undefined}
                  onClick={() => {
                    if (!props.onOpenDataNav) {
                      showToast("เปิดดูสลิปได้ที่แท็บข้อมูล รายรับ");
                      return;
                    }
                    props.onOpenDataNav(
                      item.dataNav ?? {
                        workerId: "bill-lin-income",
                        scrollTo: "transactions",
                      },
                    );
                  }}
                >
                  ดูในรายรับ
                </AeraButton>
              </div>
            ) : null}
          </div>
          {showPair ? (
            <div>
              <p className="text-sm font-medium text-aera-charcoal">
                {item.counterpartLabel}
              </p>
              <CounterpartMatchFacts item={item} />
            </div>
          ) : null}
        </div>
      </div>
    </li>
  );
}

const EVIDENCE_PREVIEW_COUNT = 2;

/** Turn closed-pair rows into the same slip + ledger timeline as ask compare. */
function flattenEvidenceForTimeline(items: EvidenceItem[]): EvidenceItem[] {
  const out: EvidenceItem[] = [];
  for (const item of items) {
    if (!item.counterpartLabel) {
      out.push(item);
      continue;
    }
    const matchedAmt =
      item.matchedAmountMinor != null
        ? formatAmountMinor(item.matchedAmountMinor)
        : (item.counterpartDetail || item.detail || "").replace(
            /^ยอดโอน\s*/,
            "",
          );
    const detailRaw = (item.detail || "").trim();
    const transferAmt =
      item.transferAmountMinor != null
        ? formatAmountMinor(item.transferAmountMinor)
        : detailRaw.replace(/^ยอดโอน\s*/, "");
    const slipDetail = transferAmt ? `ยอดโอน ${transferAmt}` : undefined;
    const remaining = item.remainingAmountMinor;
    const {
      counterpartLabel,
      counterpartDetail,
      ...slipBase
    } = item;
    out.push({
      ...slipBase,
      label: item.label || "สลิปโอนเงินจาก LINE",
      sourceLabel: item.sourceLabel || "LINE OA",
      detail: slipDetail,
      transferAmountLabel: transferAmt || undefined,
      matchedAmountLabel: matchedAmt || undefined,
      status: "complete",
    });
    out.push({
      id: `${item.id}-out`,
      label: counterpartLabel || (remaining != null && remaining > 0
        ? "ยอดค้าง"
        : "ยอดที่จะปิด"),
      status: remaining != null && remaining > 0 ? "needs_review" : "complete",
      detail:
        remaining != null && remaining > 0
          ? `ยอดค้าง ${formatAmountMinor(remaining)}`
          : matchedAmt
            ? `ยอดค้าง ${matchedAmt}`
            : counterpartDetail,
      sourceLabel: "ข้อมูลลูกหนี้",
      matchedObjectId: item.matchedObjectId,
      occurredAt: item.occurredAt,
      matchedAt: item.matchedAt,
      expiresAt: item.expiresAt,
      originalAmountMinor: item.originalAmountMinor,
      outstandingAmountMinor: item.outstandingAmountMinor,
      matchedAmountMinor: item.matchedAmountMinor,
      remainingAmountMinor: item.remainingAmountMinor,
    });
  }
  return out.map((item, index) => {
    if (
      isLedgerEvidenceItem(item) ||
      item.matchedAmountLabel
    ) {
      return item;
    }
    const ledgerRow =
      out.find(
        (candidate) =>
          isLedgerEvidenceItem(candidate) &&
          item.matchedObjectId &&
          candidate.matchedObjectId === item.matchedObjectId,
      ) ||
      out.slice(index + 1).find((candidate) => isLedgerEvidenceItem(candidate));
    const matchedAmount =
      ledgerRow?.detail?.replace(/^(?:ยอดค้าง|ยอด)\s+/, "").trim() ||
      (ledgerRow?.matchedAmountMinor != null
        ? formatAmountMinor(ledgerRow.matchedAmountMinor)
        : "");
    return matchedAmount
      ? { ...item, matchedAmountLabel: matchedAmount }
      : item;
  });
}

export function FinancialWorkEvidenceShell(props: {
  summary: LiffFinancialWorkCard["evidenceSummary"];
  items?: EvidenceItem[];
  comparisonNote?: string;
  compare?: boolean;
  caution?: boolean;
  settled?: boolean;
  focusObjectId?: string;
  focusLabel?: string;
  workspaceId?: string | null;
  onOpenDataNav?: (nav: {
    workerId: string;
    recordId?: string;
    scrollTo?: string;
    highlight?: string;
  }) => void;
}) {
  const items = props.items ?? [];
  const searchParams = useSearchParams();
  const workspaceId =
    (props.workspaceId || searchParams?.get("workspaceId") || "").trim();
  const missingItems = items.filter((item) => item.status === "missing");
  const viewable = items.filter((item) => item.status !== "missing");
  const focused =
    props.focusObjectId || props.focusLabel
      ? viewable.filter((item) => {
          if (
            props.focusObjectId &&
            item.matchedObjectId === props.focusObjectId
          ) {
            return true;
          }
          if (props.focusObjectId && item.id.includes(props.focusObjectId)) {
            return true;
          }
          if (props.focusLabel && item.label.includes(props.focusLabel)) {
            return true;
          }
          return false;
        })
      : [];
  const sourceRows = focused.length > 0 ? focused : viewable;
  // One layout: ask compare + จ่ายบางส่วน + closed matches all use the timeline.
  const rows = flattenEvidenceForTimeline(sourceRows);
  const timeline = rows.length > 0;
  const previewCount = timeline ? EVIDENCE_PREVIEW_COUNT : 1;
  const primaryRows = rows.slice(0, previewCount);
  const extraRows = rows.slice(previewCount);
  const empty = rows.length === 0 && (props.summary.complete ?? 0) === 0;
  const [viewer, setViewer] = useState<{ url: string; caption: string } | null>(
    null,
  );
  const [moreOpen, setMoreOpen] = useState(false);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const comparisonNote = props.comparisonNote?.trim() || "";
  const showBadge = Boolean(props.compare || timeline) && !props.settled;
  const railTone: "amber" | "red" = props.caution ? "red" : "amber";

  const resolveImageUrl = (item: EvidenceItem) =>
    resolveEvidenceItemImageUrl(item, workspaceId);

  const renderRows = (
    list: EvidenceItem[],
    connect?: (index: number) => {
      above: boolean;
      below: boolean;
    },
  ) =>
    list.map((item, index) => {
      const links = connect?.(index);
      return (
        <EvidenceMatchRow
          key={item.id}
          item={item}
          imageUrl={resolveImageUrl(item)}
          timeline={timeline}
          timelineConnectAbove={links?.above}
          timelineConnectBelow={links?.below}
          railTone={railTone}
          settled={props.settled}
          onPreview={(url, caption) => setViewer({ url, caption })}
          onOpenDataNav={props.onOpenDataNav}
        />
      );
    });

  return (
    <section
      className="rounded-2xl border border-aera-lightgray/80 bg-aera-white px-4 py-4"
      data-testid="fw-evidence-shell"
    >
      <div className="flex items-start justify-between gap-2">
        <h3 className="text-xs font-semibold text-aera-amberdark">
          หลักฐานที่ใช้ตัดสินใจ
        </h3>
        {showBadge ? (
          <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-aera-warmcream px-2 py-0.5 text-[10px] font-bold text-aera-amberdark">
            <CircleHelp className="h-3 w-3" aria-hidden />
            มีจุดต้องเทียบ
          </span>
        ) : props.summary.needsReview > 0 ? (
          <span className="shrink-0 rounded-md bg-amber-50 px-1.5 py-0.5 text-[10px] font-bold text-aera-amberdark">
            มีจุดต้องเทียบ
          </span>
        ) : null}
      </div>

      {comparisonNote ? (
        <p className="mt-2 text-sm font-bold leading-snug text-aera-charcoal">
          {comparisonNote}
        </p>
      ) : null}

      {props.settled && rows.length > 0 ? (
        <div className="mt-2 flex items-center gap-2 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-xs font-medium text-amber-950">
          <CircleAlert className="h-3.5 w-3.5 shrink-0 text-aera-amberdark" aria-hidden />
          <p>ปิดยอดแล้ว</p>
        </div>
      ) : null}

      {!empty
        ? missingItems.map((item) => (
        <div
          key={item.id}
          className="mt-2 rounded-xl border border-red-200 bg-red-50 px-3 py-2.5 text-xs text-red-900"
          data-testid="fw-evidence-item"
          data-status="missing"
        >
          <p className="font-semibold">{item.label}</p>
          {item.detail ? <p className="mt-0.5 text-red-800/80">{item.detail}</p> : null}
        </div>
          ))
        : null}

      {empty ? (
        <div
          className="mt-3 flex flex-col items-center px-2 py-5 text-center"
          data-testid="fw-evidence-empty"
        >
          <span className="flex h-12 w-12 items-center justify-center rounded-full bg-aera-offwhite text-aera-gray">
            <ImageIcon className="h-6 w-6" strokeWidth={1.75} aria-hidden />
          </span>
          <p className="mt-3 text-sm font-medium text-aera-charcoal">ยังไม่มีการจับคู่</p>
          <p className="mt-1 max-w-[16rem] text-xs leading-relaxed text-aera-gray">
            ยังไม่มีหลักฐานที่ถูกจับคู่กับยอดนี้ในตอนนี้
          </p>
        </div>
      ) : rows.length > 0 ? (
        <>
          {timeline ? (
            <p className="mt-3 text-[11px] font-semibold text-aera-gray">หลักฐานที่พบ</p>
          ) : null}
          <div className="relative">
            <ul
              className={timeline ? "mt-0" : "mt-3 divide-y divide-aera-lightgray/80"}
              data-testid="fw-evidence-list"
            >
              {renderRows(
                primaryRows,
                timeline
                  ? (index) => ({
                      above: index > 0,
                      below:
                        index < primaryRows.length - 1 ||
                        (moreOpen &&
                          extraRows.length > 0 &&
                          index === primaryRows.length - 1),
                    })
                  : undefined,
              )}
            </ul>
            {extraRows.length > 0 ? (
              <>
                <div
                  className={[
                    "grid transition-[grid-template-rows,opacity] duration-300 ease-out",
                    moreOpen
                      ? "grid-rows-[1fr] opacity-100"
                      : "grid-rows-[0fr] opacity-0",
                  ].join(" ")}
                  aria-hidden={!moreOpen}
                >
                  <div className="min-h-0 overflow-hidden">
                    <ul
                      className={
                        timeline ? "" : "divide-y divide-aera-lightgray/80 border-t border-aera-lightgray/80"
                      }
                    >
                      {renderRows(
                        extraRows,
                        timeline
                          ? (index) => ({
                              above: true,
                              below: index < extraRows.length - 1,
                            })
                          : undefined,
                      )}
                    </ul>
                  </div>
                </div>
                <button
                  type="button"
                  className="mt-0 flex w-full cursor-pointer items-center justify-center gap-1 text-center text-sm font-medium text-aera-amber"
                  data-testid="fw-evidence-more"
                  aria-expanded={moreOpen}
                  onClick={() => setMoreOpen((open) => !open)}
                >
                  {moreOpen
                    ? "ปิดรายการ"
                    : `ดูหลักฐานเพิ่มเติมอีก ${extraRows.length} รายการ`}
                  <ChevronDown
                    className={[
                      "h-4 w-4 transition-transform duration-300",
                      moreOpen ? "rotate-180" : "",
                    ].join(" ")}
                  />
                </button>
              </>
            ) : null}
          </div>
          {timeline ? (
            <button
              type="button"
              className="mt-1 flex w-full cursor-pointer items-center justify-center gap-1 text-center text-sm font-medium text-aera-amber"
              data-testid="fw-evidence-details"
              aria-expanded={detailsOpen}
              onClick={() => setDetailsOpen((open) => !open)}
            >
              ดูรายละเอียด
              <ChevronDown
                className={[
                  "h-4 w-4 transition-transform duration-300",
                  detailsOpen ? "rotate-180" : "",
                ].join(" ")}
              />
            </button>
          ) : null}
          {timeline && detailsOpen ? (
            sourceRows.length === 0 ? (
              <EvidenceDetailsSkeleton />
            ) : (
              <EvidenceDetailsPanel items={sourceRows} />
            )
          ) : null}
        </>
      ) : null}

      <ImageViewer
        open={Boolean(viewer)}
        imageUrl={viewer?.url ?? ""}
        caption={viewer?.caption ?? "สลิป"}
        onOpenChange={(open) => {
          if (!open) setViewer(null);
        }}
      />
    </section>
  );
}

export function FinancialWorkRecoveryPanel(props: {
  recovery: OperationRecoveryState;
  onRetry?: () => void;
}) {
  const { recovery } = props;
  // Success closes the modal and uses a toast — do not keep a committed banner here.
  if (recovery.kind === "idle" || recovery.kind === "committed") return null;

  const busy =
    recovery.kind === "submitting" || recovery.kind === "processing";
  const failed = recovery.kind === "failed";
  const message =
    recovery.kind === "submitting"
      ? "กำลังส่งรายการ…"
      : recovery.kind === "processing"
        ? recovery.message || "กำลังตรวจผลรายการ…"
        : recovery.kind === "failed"
          ? formatFinancialWorkErrorMessage(recovery.message)
          : "";

  return (
    <div
      role="status"
      data-testid="fw-recovery-panel"
      data-recovery-kind={recovery.kind}
      className={[
        "rounded-xl border px-3 py-2.5 text-xs",
        failed
          ? "border-aera-red/35 bg-aera-redlight"
          : "border-aera-lightgray bg-aera-offwhite text-aera-charcoal",
      ].join(" ")}
    >
      {failed ? (
        <p className="font-semibold text-aera-red">บันทึกไม่สำเร็จ</p>
      ) : null}
      <p className={failed ? "mt-1 text-aera-charcoal" : undefined}>{message}</p>
      {recovery.kind === "failed" && recovery.retrySafeWithSameKey && props.onRetry ? (
        <AeraButton
          type="button"
          variant="secondary"
          size="xs"
          className="mt-2 cursor-pointer"
          data-testid="fw-recovery-retry"
          disabled={busy}
          onClick={props.onRetry}
        >
          ตรวจสถานะด้วยรหัสเดิม
        </AeraButton>
      ) : null}
    </div>
  );
}

export function FinancialWorkDetailModal(props: {
  open: boolean;
  card: LiffFinancialWorkCard | null;
  missing?: boolean;
  loading?: boolean;
  recovery: OperationRecoveryState;
  onClose: () => void;
  onExited?: () => void;
  onAction: (action: ActionDirective) => void;
  onRetryRecovery?: () => void;
  lineEntryRevision?: number | null;
  onAckLineStale?: () => void;
  onReloadProjection?: () => void;
  focusPanel?: "outstanding" | "children" | null;
  onOpenChild?: (objectRef: { kind: string; id: string }) => void;
  onBackFromChase?: () => void;
  childOverlay?: LiffFinancialWorkCard | null;
  onCloseChild?: () => void;
  onOpenDataNav?: (nav: {
    workerId: string;
    recordId?: string;
    scrollTo?: string;
    highlight?: string;
  }) => void;
  workspaceId?: string | null;
  ownerUserId?: string | null;
  pendingSlipItems?: ReceivableListItem[];
  pendingAnnouncementItems?: ReceivableListItem[];
  pendingSlipsLoading?: boolean;
  onPendingSlipMatched?: () => void;
}) {
  const { canMutate, blockedReason } = useFinancialWorkMutationGate();
  const searchParams = useSearchParams();
  const [keyboardInset, setKeyboardInset] = useState(0);
  const [selectedAskId, setSelectedAskId] = useState<string | null>(null);
  const [selectedPendingSlipKey, setSelectedPendingSlipKey] = useState<
    string | null
  >(null);
  const [pendingSlipMatchBusy, setPendingSlipMatchBusy] = useState(false);
  const [slipMatchRecovery, setSlipMatchRecovery] =
    useState<OperationRecoveryState>({ kind: "idle" });
  const pending = Boolean(props.loading);
  const title = displayWorkTitle(props.card?.title) || "รายละเอียดงานเงิน";
  const closedCard = Boolean(props.card?.requirementStatus?.complete);
  const askCard = props.card ? isSlipOwnerAskCard(props.card) : false;
  const askKind = props.card ? askKindFromCard(props.card) : null;
  const waitingReceivable = isWaitingReceivableCard(props.card);
  const pendingSlips = useMemo(
    () =>
      uniquePendingSlipItems(
        filterReceivableMoneyCandidates(props.pendingSlipItems ?? []),
      ),
    [props.pendingSlipItems],
  );
  const pendingAnnouncements = useMemo(
    () =>
      filterReceivableMoneyCandidates(props.pendingAnnouncementItems ?? []),
    [props.pendingAnnouncementItems],
  );
  const displayRecovery =
    slipMatchRecovery.kind !== "idle" ? slipMatchRecovery : props.recovery;
  const cardKey = props.card
    ? askOptionKey(props.card.objectRef)
    : "";
  // open_data stays off the ยืนยัน footer for ask/closed cards. Waiting
  // (กำลังถือ) uses it as "ดูในข้อมูล" so the drawer matches ต้องให้คุณทำ.
  // Ask cards: select a ยอด first, then confirm from the sticky footer — except
  // single-tap LINE cards (#83 confirm, #86 park, #87 split, #88.1 retro).
  const primary = props.card?.availableActions.find((a) => {
    if (
      a.actionType === "open_detail" ||
      a.actionType === "open_outstanding" ||
      a.actionType === "open_data"
    ) {
      return false;
    }
    if (askCard && a.actionType === "confirm_match") return false;
    if (waitingReceivable && pendingSlips.length > 0) return false;
    if (closedCard) {
      const label = a.label || "";
      if (
        label.includes("จับคู่") ||
        a.actionType.includes("correct") ||
        a.actionType.includes("match") ||
        a.actionType.includes("resplit")
      ) {
        return false;
      }
    }
    return true;
  });
  const viewDataAction =
    !askCard && !closedCard
      ? props.card?.availableActions.find((a) => a.actionType === "open_data")
      : undefined;
  const actionBusy =
    displayRecovery.kind === "submitting" ||
    displayRecovery.kind === "processing" ||
    pendingSlipMatchBusy;
  const chaseOpen =
    props.open &&
    props.focusPanel === "outstanding" &&
    Boolean(props.card?.outstanding);
  const detailOpen = props.open && !chaseOpen;

  const childOpen = Boolean(props.childOverlay);
  const requestCloseParent = () => {
    if (childOpen) {
      props.onCloseChild?.();
      return;
    }
    props.onClose();
  };

  useEffect(() => {
    setSelectedAskId((current) => {
      const rows = props.card?.consequenceRows ?? [];
      if (
        askKind === "slip_confirm" ||
        (askKind === "retro_match" && rows.length === 1)
      ) {
        return rows[0] ? askOptionKey(rows[0].objectRef) : null;
      }
      if (!current) return null;
      const stillThere = rows.some(
        (row) => askOptionKey(row.objectRef) === current,
      );
      return stillThere ? current : null;
    });
  }, [cardKey, props.card, askKind]);

  useEffect(() => {
    setSelectedPendingSlipKey((current) => {
      if (!current) return null;
      return pendingSlips.some(
        (slip, index) => pendingSlipOptionId(slip, index) === current,
      )
        ? current
        : null;
    });
  }, [cardKey, pendingSlips]);

  useEffect(() => {
    setSlipMatchRecovery({ kind: "idle" });
  }, [cardKey]);

  const selectedPendingSlip = selectedPendingSlipKey
    ? pendingSlips.find(
        (slip, index) =>
          pendingSlipOptionId(slip, index) === selectedPendingSlipKey,
      )
    : undefined;

  const confirmPendingSlipMatch = async () => {
    const owner = props.ownerUserId?.trim();
    const workspace = props.workspaceId?.trim();
    const receivableOrderId = props.card
      ? receivableOrderIdForMatch(props.card, pendingAnnouncements)
      : null;
    const paymentOrderId = selectedPendingSlip
      ? getReceivableOrderId(selectedPendingSlip)
      : null;
    const operationKey = "fw:pending-slip-match";
    if (!owner || !receivableOrderId || !paymentOrderId) {
      const missing = [
        !owner ? "เจ้าของร้าน" : null,
        !receivableOrderId ? "ยอดค้าง" : null,
        !paymentOrderId ? "สลิปที่เลือก" : null,
      ].filter(Boolean);
      setSlipMatchRecovery({
        kind: "failed",
        operationKey,
        message: `จับคู่ไม่สำเร็จ — ไม่พบ${missing.join(" / ")}`,
        retrySafeWithSameKey: false,
      });
      return;
    }
    setPendingSlipMatchBusy(true);
    setSlipMatchRecovery({ kind: "submitting", operationKey });
    try {
      const result = await matchReceivablePayment(owner, receivableOrderId, [
        paymentOrderId,
      ]);
      const operationId =
        (typeof result.operation_id === "string" && result.operation_id.trim()) ||
        (result.after &&
        typeof result.after === "object" &&
        typeof (result.after as { operation_id?: unknown }).operation_id ===
          "string"
          ? String(
              (result.after as { operation_id?: string }).operation_id,
            ).trim()
          : null);
      setSlipMatchRecovery({ kind: "idle" });
      offerMoneyUndoToast({
        message: "จับคู่สำเร็จ",
        sourceLabel: pickMoneyUndoSourceLabel(selectedPendingSlip),
        ownerUserId: owner,
        workspaceId: workspace || "",
        operationId,
        onUndone: () => {
          props.onPendingSlipMatched?.();
          props.onReloadProjection?.();
        },
      });
      notifyIncomeMoneyChanged();
      props.onPendingSlipMatched?.();
      props.onReloadProjection?.();
      props.onClose();
    } catch (err) {
      setSlipMatchRecovery({
        kind: "failed",
        operationKey,
        message:
          err instanceof Error && err.message.trim()
            ? err.message.trim()
            : "จับคู่ไม่สำเร็จ กรุณาลองใหม่",
        retrySafeWithSameKey: false,
      });
    } finally {
      setPendingSlipMatchBusy(false);
    }
  };

  const selectedAskRow =
    selectedAskId && props.card
      ? props.card.consequenceRows.find(
          (row) => askOptionKey(row.objectRef) === selectedAskId,
        )
      : undefined;
  const selectedAskAction =
    selectedAskId && props.card
      ? props.card.availableActions.find(
          (action) =>
            action.actionType === "confirm_match" &&
            askOptionKey(action.objectRef) === selectedAskId,
        )
      : undefined;
  const confirmMatches = (props.card?.availableActions ?? []).filter(
    (action) => action.actionType === "confirm_match",
  );
  const splitConfirm = props.card?.availableActions.find(
    (action) => action.actionType === "confirm_split",
  );
  const parkAction = props.card?.availableActions.find(
    (action) => action.actionType === "park_slip",
  );
  const rejectAction = props.card?.availableActions.find(
    (action) =>
      action.actionType === "reject_split" ||
      action.actionType === "reject_retro",
  );
  const matchNowAction =
    askKind === "unmatched_slip" && confirmMatches.length === 0
      ? props.card?.availableActions.find(
          (action) => action.actionType === "open_data",
        )
      : undefined;
  const pickThenConfirm =
    askKind === "slip_owner_select" ||
    askKind === "unmatched_slip" ||
    (askKind === "retro_match" && confirmMatches.length > 1);
  const footerConfirmAction = pickThenConfirm
    ? selectedAskAction
    : splitConfirm ||
      (confirmMatches.length === 1 ? confirmMatches[0] : selectedAskAction) ||
      matchNowAction;
  const footerDismissAction = parkAction || rejectAction;
  const footerDismissLabel =
    footerDismissAction?.label || "ไว้ก่อน";
  const askConfirmLabel = pickThenConfirm
    ? selectedAskRow
      ? `ยืนยัน: ปิด${askOptionDisplayName(selectedAskRow.label)}`
      : "ยืนยัน: ปิดยอดที่เลือก"
    : matchNowAction
      ? matchNowAction.label || "จับคู่ตอนนี้"
      : splitConfirm
        ? splitConfirm.label || "ยืนยันแบ่งยอด"
        : selectedAskRow
          ? `ยืนยัน: ปิด${askOptionDisplayName(selectedAskRow.label)}`
          : footerConfirmAction?.label || "ยืนยัน: ปิดยอดที่เลือก";

  useLayoutEffect(() => {
    const forcedRaw = searchParams?.get("fwKeyboardInset")?.trim() || "";
    const forced = Number(forcedRaw);
    if (Number.isFinite(forced) && forced > 0) {
      setKeyboardInset(forced);
      return;
    }

    const vv = typeof window !== "undefined" ? window.visualViewport : null;
    if (!vv) {
      setKeyboardInset(0);
      return;
    }

    const update = () => {
      const viewportHeight = window.innerHeight || 0;
      const visibleBottom = vv.height + vv.offsetTop;
      const inset = Math.max(0, Math.round(viewportHeight - visibleBottom));
      setKeyboardInset(inset > 24 ? inset : 0);
    };

    update();
    vv.addEventListener("resize", update);
    vv.addEventListener("scroll", update);
    window.addEventListener("resize", update);
    return () => {
      vv.removeEventListener("resize", update);
      vv.removeEventListener("scroll", update);
      window.removeEventListener("resize", update);
    };
  }, [searchParams]);

  const staleCard = props.card?.freshness === "stale";
  const failedRecovery = displayRecovery.kind === "failed";
  const confirmLabel = failedRecovery
    ? "ลองอีกครั้ง"
    : staleCard
      ? "ข้อมูลเปลี่ยนแปลงแล้ว"
      : primary
        ? primary.label?.startsWith("ยืนยัน")
          ? primary.label
          : `ยืนยัน: ${primary.label || "ทำรายการ"}`
        : null;
  const footerInset = {
    paddingBottom: keyboardInset > 0 ? `${keyboardInset}px` : undefined,
  };

  return (
    <>
      <Modal
        isOpen={detailOpen}
        onClose={requestCloseParent}
        onExited={props.onExited}
        size="md"
        placement="responsive-drawer"
        zIndexClass="z-[10050]"
        title={title}
        header={
          <div className="relative z-20 h-0 overflow-visible">
            <div className="absolute right-3 top-3 sm:right-4">
              <ModalCloseButton onClose={requestCloseParent} />
            </div>
          </div>
        }
        bodyClassName={closedCard ? "bg-aera-offwhite" : "bg-aera-white"}
        sheetHost
        panelClassName={childOpen ? "!overflow-hidden" : undefined}
        preventBackdropClose={childOpen}
        footerClassName={
          closedCard
            ? "bg-aera-offwhite pb-[max(1rem,env(safe-area-inset-bottom))]"
            : "bg-aera-white pb-[max(1rem,env(safe-area-inset-bottom))]"
        }
        footer={
          pending ? (
            <div
              data-testid="fw-detail-footer-skeleton"
              className="w-full"
              style={footerInset}
            >
              <div
                className="h-11 w-full animate-pulse rounded-xl bg-aera-lightgray/80"
                aria-hidden
              />
            </div>
          ) : props.missing || !props.card ? (
            <div className="w-full" style={footerInset}>
              <div className="grid grid-cols-2 gap-2">
                <AeraButton
                  type="button"
                  variant="outline"
                  size="md"
                  fullWidth
                  className="min-w-0 cursor-pointer"
                  data-testid="fw-sticky-action-button"
                  onClick={requestCloseParent}
                >
                  ปิด
                </AeraButton>
                <AeraButton
                  type="button"
                  variant="secondary"
                  size="md"
                  fullWidth
                  className="min-w-0 cursor-pointer"
                  data-testid="fw-detail-missing-reload"
                  onClick={() => props.onReloadProjection?.()}
                  disabled={!props.onReloadProjection}
                >
                  โหลดใหม่
                </AeraButton>
              </div>
            </div>
          ) : closedCard && !primary ? (
            <div className="w-full" style={footerInset}>
              <AeraButton
                type="button"
                variant="lightgray"
                size="md"
                className="w-full cursor-pointer"
                data-testid="fw-sticky-action-button"
                onClick={requestCloseParent}
              >
                ปิดรายละเอียด
              </AeraButton>
            </div>
          ) : waitingReceivable && pendingSlips.length > 0 ? (
            <div
              data-testid="fw-sticky-action"
              className="w-full"
              style={footerInset}
            >
              {!canMutate ? (
                <p className="mb-1.5 text-center text-[11px] text-aera-gray">
                  {blockedReason}
                </p>
              ) : null}
              <div className="grid grid-cols-2 gap-2">
                <AeraButton
                  type="button"
                  variant="outline"
                  size="md"
                  fullWidth
                  className="min-w-0 cursor-pointer"
                  data-testid="fw-sticky-action-button"
                  onClick={requestCloseParent}
                >
                  ไว้ก่อน
                </AeraButton>
                <AeraButton
                  type="button"
                  variant="secondary"
                  size="md"
                  fullWidth
                  className="min-w-0 cursor-pointer whitespace-normal leading-snug"
                  data-testid="fw-pending-slip-confirm"
                  disabled={
                    Boolean(staleCard) ||
                    actionBusy ||
                    (!failedRecovery &&
                      (!selectedPendingSlip || !canMutate))
                  }
                  title={!canMutate ? blockedReason : undefined}
                  onClick={() => {
                    void confirmPendingSlipMatch();
                  }}
                >
                  {failedRecovery
                    ? "ลองอีกครั้ง"
                    : selectedPendingSlip
                      ? `ยืนยัน: จับคู่${askOptionDisplayName(pendingSlipOptionLabel(selectedPendingSlip))}`
                      : "ยืนยัน: จับคู่สลิปที่เลือก"}
                </AeraButton>
              </div>
            </div>
          ) : askCard ? (
            <div
              data-testid="fw-sticky-action"
              className="w-full"
              style={footerInset}
            >
              {!canMutate ? (
                <p className="mb-1.5 text-center text-[11px] text-aera-gray">
                  {blockedReason}
                </p>
              ) : null}
              <div className="grid grid-cols-2 gap-2">
                <AeraButton
                  type="button"
                  variant="outline"
                  size="md"
                  fullWidth
                  className="min-w-0 cursor-pointer"
                  data-testid="fw-sticky-action-button"
                  disabled={Boolean(footerDismissAction) && (!canMutate || actionBusy)}
                  title={
                    footerDismissAction && !canMutate ? blockedReason : undefined
                  }
                  onClick={() => {
                    if (footerDismissAction) {
                      props.onAction(footerDismissAction);
                      return;
                    }
                    requestCloseParent();
                  }}
                >
                  {footerDismissLabel}
                </AeraButton>
                <AeraButton
                  type="button"
                  variant="secondary"
                  size="md"
                  fullWidth
                  className="min-w-0 cursor-pointer whitespace-normal leading-snug"
                  data-testid="fw-ask-confirm-selected"
                  disabled={
                    Boolean(staleCard) ||
                    actionBusy ||
                    (!failedRecovery &&
                      (!footerConfirmAction ||
                        (footerConfirmAction.actionType !== "open_data" &&
                          !canMutate)))
                  }
                  title={!canMutate ? blockedReason : undefined}
                  onClick={() => {
                    if (failedRecovery && footerConfirmAction) {
                      props.onAction(footerConfirmAction);
                      return;
                    }
                    if (failedRecovery) {
                      props.onRetryRecovery?.();
                      return;
                    }
                    if (footerConfirmAction) props.onAction(footerConfirmAction);
                  }}
                >
                  {failedRecovery ? "ลองอีกครั้ง" : askConfirmLabel}
                </AeraButton>
              </div>
            </div>
          ) : primary || confirmLabel || viewDataAction ? (
            <div
              data-testid="fw-sticky-action"
              data-fw-keyboard-inset={String(keyboardInset)}
              className="w-full"
              style={footerInset}
            >
              {!canMutate && primary && primary.actionType !== "open_data" ? (
                <p className="mb-1.5 text-center text-[11px] text-aera-gray">
                  {blockedReason}
                </p>
              ) : null}
              <div className="grid grid-cols-2 gap-2">
                <AeraButton
                  type="button"
                  variant="outline"
                  size="md"
                  fullWidth
                  className="min-w-0 cursor-pointer"
                  onClick={requestCloseParent}
                >
                  ไว้ก่อน
                </AeraButton>
                <AeraButton
                  type="button"
                  variant="secondary"
                  size="md"
                  fullWidth
                  className="min-w-0 cursor-pointer"
                  disabled={
                    Boolean(staleCard) ||
                    actionBusy ||
                    Boolean(
                      primary &&
                        primary.actionType !== "open_data" &&
                        !canMutate &&
                        !failedRecovery,
                    )
                  }
                  data-testid="fw-sticky-action-button"
                  onClick={() => {
                    if (failedRecovery) {
                      props.onRetryRecovery?.();
                      return;
                    }
                    if (primary) {
                      props.onAction(primary);
                      return;
                    }
                    if (viewDataAction) props.onAction(viewDataAction);
                  }}
                >
                  {confirmLabel ||
                    primary?.label ||
                    viewDataAction?.label ||
                    "ทำรายการ"}
                </AeraButton>
              </div>
            </div>
          ) : undefined
        }
      >
        <div data-testid="fw-detail-modal" className="min-w-0">
          <FinancialWorkDetailView
            card={props.card}
            missing={props.missing}
            loading={props.loading}
            recovery={displayRecovery}
            onAction={props.onAction}
            onRetryRecovery={
              slipMatchRecovery.kind === "failed"
                ? () => {
                    void confirmPendingSlipMatch();
                  }
                : props.onRetryRecovery
            }
            lineEntryRevision={props.lineEntryRevision}
            onAckLineStale={props.onAckLineStale}
            onReloadProjection={props.onReloadProjection}
            focusPanel={props.focusPanel}
            onOpenChild={props.onOpenChild}
            onBackFromChase={props.onBackFromChase}
            embeddedInModal
            onOpenDataNav={props.onOpenDataNav}
            workspaceId={props.workspaceId}
            selectedAskId={selectedAskId}
            onSelectAskOption={setSelectedAskId}
            pendingSlipItems={props.pendingSlipItems}
            selectedPendingSlipKey={selectedPendingSlipKey}
            onSelectPendingSlip={setSelectedPendingSlipKey}
            pendingSlipsLoading={props.pendingSlipsLoading}
            ownerUserId={props.ownerUserId}
          />
        </div>
      </Modal>

      <FinancialWorkOutstandingSheet
        open={chaseOpen}
        card={props.card}
        onClose={requestCloseParent}
        onAction={props.onAction}
        onOpenChild={props.onOpenChild}
        onReload={props.onReloadProjection}
        preventBackdropClose={childOpen}
      />

      <FinancialWorkChildOverlay
        open={childOpen}
        card={props.childOverlay ?? null}
        onClose={() => props.onCloseChild?.()}
        onOpenDataNav={props.onOpenDataNav}
      />
    </>
  );
}

export function FinancialWorkStickyAction(props: {
  label: string;
  disabled?: boolean;
  disabledReason?: string;
  onClick: () => void;
}) {
  const searchParams = useSearchParams();
  const [keyboardInset, setKeyboardInset] = useState(0);

  useLayoutEffect(() => {
    const forcedRaw = searchParams?.get("fwKeyboardInset")?.trim() || "";
    const forced = Number(forcedRaw);
    if (Number.isFinite(forced) && forced > 0) {
      setKeyboardInset(forced);
      return;
    }

    const vv = typeof window !== "undefined" ? window.visualViewport : null;
    if (!vv) {
      setKeyboardInset(0);
      return;
    }

    const update = () => {
      const viewportHeight = window.innerHeight || 0;
      const visibleBottom = vv.height + vv.offsetTop;
      const inset = Math.max(0, Math.round(viewportHeight - visibleBottom));
      setKeyboardInset(inset > 24 ? inset : 0);
    };

    update();
    vv.addEventListener("resize", update);
    vv.addEventListener("scroll", update);
    window.addEventListener("resize", update);
    return () => {
      vv.removeEventListener("resize", update);
      vv.removeEventListener("scroll", update);
      window.removeEventListener("resize", update);
    };
  }, [searchParams]);

  return (
    <div
      className="pointer-events-none fixed inset-x-0 bottom-0 z-40 flex justify-center px-3"
      style={{
        bottom: keyboardInset > 0 ? `${keyboardInset}px` : undefined,
        paddingBottom: "max(0.75rem, env(safe-area-inset-bottom, 0px))",
      }}
      data-testid="fw-sticky-action"
      data-fw-keyboard-inset={String(keyboardInset)}
    >
      <div className="pointer-events-auto w-full max-w-lg">
        {props.disabled && props.disabledReason ? (
          <p className="mb-1.5 text-center text-[11px] text-aera-gray">
            {props.disabledReason}
          </p>
        ) : null}
        <AeraButton
          variant="primary"
          size="md"
          className="w-full shadow-lg"
          disabled={props.disabled}
          data-testid="fw-sticky-action-button"
          onClick={props.onClick}
        >
          {props.label}
        </AeraButton>
      </div>
    </div>
  );
}

export function FinancialWorkSourcesPlaceholder() {
  return (
    <div
      data-testid="fw-sources-placeholder"
      className="space-y-4 rounded-2xl border border-dashed border-aera-lightgray bg-aera-offwhite px-4 py-5 text-sm text-aera-gray"
    >
      <p className="font-medium text-aera-charcoal">แหล่งข้อมูล/การแชร์</p>
      <section>
        <h2 className="text-sm font-semibold text-aera-charcoal">แหล่งข้อมูล</h2>
        <p className="mt-1">ยังไม่มีแหล่งข้อมูลในร้านนี้</p>
      </section>
      <section>
        <h2 className="text-sm font-semibold text-aera-charcoal">แชร์โดยฉัน</h2>
        <p className="mt-1">ยังไม่มีรายการที่คุณแชร์</p>
      </section>
      <section>
        <h2 className="text-sm font-semibold text-aera-charcoal">แชร์กับฉัน</h2>
        <p className="mt-1">ยังไม่มีรายการที่แชร์กับคุณ</p>
      </section>
    </div>
  );
}

export function buildFinancialWorkHref(
  currentSearch: string,
  patch: Record<string, string | null>,
): string {
  const params = new URLSearchParams(
    currentSearch.startsWith("?") ? currentSearch.slice(1) : currentSearch,
  );
  for (const [key, value] of Object.entries(patch)) {
    if (value == null || value === "") params.delete(key);
    else params.set(key, value);
  }
  const normalized = normalizeFinancialWorkSearch(params.toString());
  const q = normalized.next;
  return q ? `?${q}` : "?";
}
